import {
  Close as CloseIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  HelpOutline as HelpOutlineIcon
} from "@mui/icons-material";
import {
  Box,
  Typography,
  Paper,
  Chip,
  IconButton,
  Card,
  CardContent,
  Tabs,
  Tab,
  Button,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  FormControlLabel,
  Tooltip,
  Checkbox,
  Autocomplete,
  CircularProgress,
  Drawer,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { DatePicker } from "@mui/x-date-pickers";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios, { AxiosError } from "axios";
import { TruckIcon } from "lucide-react";
import { enqueueSnackbar } from "notistack";
import { HTMLAttributes, Key, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { gToKg, mm3ToM3 } from "src/components/ShipmentOrderLegRunSplitForm/splitAllocation";
import LicenseContext from "src/contexts/LicenseContext";
import datetimeDecorator from "src/decorators/datetime.decorator";
import {
  DesignatedTruckCarModelOptions,
  DesignatedTruckFeatureOptions,
  DesignatedTruckFloorSpecificationOptions,
  DesignatedTruckKlassOptions,
  DesignatedTruckLoadingPlatformHeightOptions,
  DesignatedTruckLoadingPlatformLengthOptions,
  DesignatedTruckLoadingPlatformWidthOptions,
  ItemHandlingOfCargoStyleOptions,
  ItemKlassOptions,
  ItemPackageStyleOptions,
  TruckDriverIdAndName
} from "src/entities/orderEntity";
import { PlaceEntity } from "src/entities/placeEntity";
import { ProductEntity } from "src/entities/ProductEntity";
import {
  ShipmentDetailFormData,
  ShipmentOrderBillingAmountEntity,
  ShipmentOrderItemsEntity,
  ShipmentOrderLegRunPaymentAmountEntity,
} from "src/entities/ShipmentOrderResponse.entity";
import { ShipperEntity } from "src/entities/shipperEntity";
import { useDriversQuery } from 'src/hooks/useDrivers.query';
import { usePartnerCompaniesRequest } from "src/hooks/usePartnerCompanies.request";
import { useInfiniteProductsRequest } from "src/hooks/useProducts.request";
import { useQueryCompany } from "src/hooks/useQueryCompany";
import { useQueryPlaces } from "src/hooks/useQueryPlaces";
import { useMutationShipmentOrder, useQueryShipmentOrder } from "src/hooks/useQueryShipmentOrders";
import { useQueryShippers } from "src/hooks/useQueryShippers";
import { useMutationShipmentOrderLegRun } from "src/hooks/useShipmentOrderLegRun";
import { useTrucksQuery } from 'src/hooks/useTrucks.query';
import { computeAutoFillItemWeightVolume } from "src/utils/autoFillItemWeightVolume";
import {
  buildShipmentLegRunItems,
  getEarliestDateFormatted,
  ShipmentLegRunItem,
} from "src/utils/shipmentLegRunItems";
import {
  LOADING_UNLOADING_DATE_RANGE_ERROR_MESSAGE,
} from "src/utils/shipmentOrder.util";
import {
  processWaypointTimes,
  validateLoadingUnloadingDateRangeForWaypointTimes,
} from "src/utils/shipmentOrderWaypointTimes";
import {
  findSplitLeg,
  nextClientKey,
  splitRunsOf,
} from "src/utils/splitShipmentItems";

import { ConfirmDialog } from "../ConfirmDialog";
import { ShipperOption } from "../selectors/ShipperOption";
import { TruckDriverConstraintsSelector } from "../ui/shipmentOrders/TruckDriverConstraintsSelector";

import { ShipmentLegRunListCard } from "./ShipmentLegRunListCard";

// API リクエスト用の sanitized 型。画面 state 専用フィールド (entity / *ManuallyEdited)
// と、virtual setter (weightKg / volumeM3) で派生する DB column (weightG / volumeMm3) を
// 除外する。後者を含めると nested attributes の setter 順序で virtual setter による
// 更新が打ち消され、dirty tracking が崩れて save_product callback が発火しなくなる
// (log_web_issues #239 の伝票詳細での再現バグ対策)。
// TODO: `ShipmentDetailFormData` 自体が画面 state と API payload の両方を表しており、
// `useMutationShipmentOrder.updateShipmentOrder` の引数型も含めて将来分離する。
type ShipmentOrderItemPayload = Omit<
  ShipmentOrderItemsEntity,
  'entity' | 'weightKgManuallyEdited' | 'volumeM3ManuallyEdited' | 'weightG' | 'volumeMm3'
> & { _destroy?: boolean };

interface ShipmentDetailProps {
  shipmentId: number;
  editShipmentDialogOpen: boolean;
  onClose: () => void;
  startOn: string;
  editOnOpen?: boolean;
}

// 分割表示の各テーブル（編集/read-only/伝票合計）で列幅を揃えるための共通定義。
// MUI Table の colgroup に適用して全テーブルの列位置を一致させる。
// props 非依存の静的定数なのでモジュールトップレベルに置く（再レンダーで再生成しない）。
const SPLIT_ITEM_COLUMNS: { label: string; width: number; align?: 'right' }[] = [
  { label: '商品コード', width: 140 },
  { label: '品名', width: 240 },
  { label: '荷姿', width: 160 },
  { label: '数量', width: 100, align: 'right' },
  { label: '重量(kg)', width: 150, align: 'right' },
  { label: '体積(㎥)', width: 150, align: 'right' },
  { label: 'ロットNo', width: 160 },
  { label: '特注No', width: 140 },
];
const SPLIT_ITEM_TOTAL_WIDTH = SPLIT_ITEM_COLUMNS.reduce((sum, c) => sum + c.width, 0);
const SPLIT_ACTION_COL_WIDTH = 160;

export const ShipmentDetail = ({
  shipmentId,
  editShipmentDialogOpen,
  onClose,
  startOn,
  editOnOpen = false,
}: ShipmentDetailProps) => {
  const queryClient = useQueryClient();
  const timeSlotOptions = ["AM", "PM", "ALL", "カスタム"];

  const licenseContext = useContext(LicenseContext);
  const [confirmDiaalogOpen, setConfirmDialogOpen] = useState(false);
  const [modified, setModified] = useState(false);

  const [companyId, setCompanyId] = useState(0);
  const [detailTab, setDetailTab] = useState(0);
  const [isEditing, setIsEditing] = useState(editOnOpen);
  // 保存ボタンとキャンセルボタンのローディング状態を管理
  const [isSaving, setIsSaving] = useState(false);

  const [totalBillingAmount, setTotalBillingAmount] = useState(0);
  const [editableLegRunItems, setEditableLegRunItems] = useState<ShipmentLegRunItem[]>([]);

  const { register, handleSubmit, getValues, setValue, reset, watch, formState, control } = useForm<ShipmentDetailFormData>();

  const [customInputFields, setCustomInputFields] = useState<string[]>([]);
  const [products, setProducts] = useState<ProductEntity[]>([]);
  const [shippers, setShippers] = useState<ShipperEntity[]>([]);
  const [places, setPlaces] = useState<PlaceEntity[]>([]);
  const pickupPlaces = places.filter(p => !p.location_role || p.location_role !== 'unloading');
  const deliveryPlaces = places.filter(p => !p.location_role || p.location_role !== 'loading');
  const [timeSettings, setTimeSettings] = useState<{ kind: string, startAt: string, endAt: string }[]>([
    { kind: 'AM', startAt: '08:00', endAt: '12:00' },
    { kind: 'PM', startAt: '13:00', endAt: '18:00' },
    { kind: 'ALL', startAt: '08:00', endAt: '20:00' },
  ]);


  const {
    fields: itemFields,
    append: appendItem,
    remove: removeItem
  } = useFieldArray({
    control,
    name: "shipmentOrderItems"
  });

  const {
    fields: loadingTimeFields,
    update: updateLoadingTime,
    append: appendLoadingTime,
    remove: removeLoadingTime
  } = useFieldArray({
    control,
    name: "shipmentOrderLoadingWaypoint.shipmentOrderWaypointLoadingTimes"
  });

  const {
    fields: unloadingTimeFields,
    update: updateUnloadingTime,
    append: appendUnloadingTime,
    remove: removeUnloadingTime
  } = useFieldArray({
    control,
    name: "shipmentOrderUnloadingWaypoint.shipmentOrderWaypointUnloadingTimes"
  });

  useEffect(() => {
    setModified(formState.isDirty);
  }, [formState.isDirty]);

  useEffect(() => {
    if (!editShipmentDialogOpen) return;
    setModified(false);
  }, [editShipmentDialogOpen, reset]);

  const handleClose = () => {
    if (modified) {
      setConfirmDialogOpen(true);
    } else {
      onClose();
    }
  };

  const handleConfirmOK = () => {
    setConfirmDialogOpen(false);
    onClose();
  };

  const handleConfirmCancel = () => {
    setConfirmDialogOpen(false);
  };

  const { data: shipmentData, } = useQueryShipmentOrder(shipmentId);
  const { data: companyData } = useQueryCompany(companyId);
  const { updateShipmentOrder } = useMutationShipmentOrder();
  const { restoreSplitLegRun } = useMutationShipmentOrderLegRun();
  const deleteShipper = useMutation({
    mutationFn: (shipperId: number) => axios.delete(`/api/v2/shippers/${shipperId}`),

    onSuccess: async (_, shipperId) => {
      const deletedShipper = shippers.find((shipper) => shipper.id === shipperId);
      if (deletedShipper && getValues("shipperName") === deletedShipper.name) {
        setValue("shipperName", "", { shouldDirty: true });
        setValue("shipperContactPerson", "", { shouldDirty: true });
        setValue("shipperPhoneNumber", "", { shouldDirty: true });
        setValue("shipperEmailAddress", "", { shouldDirty: true });
      }
      setShippers((current) => current.filter((shipper) => shipper.id !== shipperId));
      await queryClient.invalidateQueries({
        queryKey: ['shippers']
      });
    }
  });
  const {
    data: productsData,
    isLoading: productsIsLoading,
    hasNextPage: productsHasNextPage,
    fetchNextPage: fetchNextProductsPage,
    isFetchingNextPage: productsIsFetchingNextPage
  } = useInfiniteProductsRequest('', '', editShipmentDialogOpen, 100);
  const shippersQuery = useQueryShippers(true);
  const placesQuery = useQueryPlaces(true);
  const { data: partnerCompanyData } = usePartnerCompaniesRequest(0, '', '', true, 5000);
  const { data: driverData } = useDriversQuery(startOn, { enabled: editShipmentDialogOpen });
  const { data: truckData } = useTrucksQuery(startOn, { enabled: editShipmentDialogOpen });

  useEffect(() => {
    if (productsData) {
      const allProducts = productsData.pages.flatMap((page) => page.data);
      setProducts(allProducts);
    }
    if (productsHasNextPage && !productsIsFetchingNextPage) {
      void fetchNextProductsPage();
    }
  }, [productsData, productsHasNextPage, productsIsFetchingNextPage, fetchNextProductsPage]);

  useEffect(() => {
    if (shippersQuery.data) {
      const allShippers = shippersQuery.data.pages.flatMap(page => page.data);
      // 名前の重複を除去（最初に見つかったもので重複を除外）
      const uniqueShippers = allShippers.filter((shipper, index, array) =>
        array.findIndex(s => s.name === shipper.name) === index
      );
      setShippers(uniqueShippers);
    }
    if (shippersQuery.hasNextPage && !shippersQuery.isFetchingNextPage) {
      void shippersQuery.fetchNextPage();
    }
  }, [shippersQuery.data, shippersQuery.hasNextPage, shippersQuery.isFetchingNextPage]);

  useEffect(() => {
    if (placesQuery.data) {
      const allPlaces = placesQuery.data.pages.flatMap(page => page.data);
      // 名前の重複を除去（最初に見つかったもので重複を除外）
      const uniquePlaces = allPlaces.filter((place, index, array) =>
        array.findIndex(p => p.name === place.name) === index
      );
      setPlaces(uniquePlaces);
    }
    if (placesQuery.hasNextPage && !placesQuery.isFetchingNextPage) {
      void placesQuery.fetchNextPage();
    }
  }, [placesQuery.data, placesQuery.hasNextPage, placesQuery.isFetchingNextPage]);

  useEffect(() => {
    if (!companyData) return;
    const ranges = companyData.company_order_time_ranges_attributes || [];

    setTimeSettings((prev) => prev.map((it) => {
        const range = ranges.find((rng) => it.kind === rng.kind);
        if (range) {
          return {
            ...it,
            startAt: range.start_at,
            endAt: range.end_at,
          };
        }
        return it;
      }));
    setCustomInputFields(companyData.custom_input_fields);
  }, [companyData]);

  useEffect(() => {
    if (!licenseContext.config) return;

    const config = licenseContext.config;
    setCompanyId(config.selected_company_id || config.company_id)
  }, [licenseContext.config]);

  const calculateTotalBillingAmount = useCallback(() => {
    const total = editableLegRunItems.reduce((sum, item) => sum + (item.billingAmountTotal || 0), 0);
    setTotalBillingAmount(total);
  }, [editableLegRunItems]);

  useEffect(() => {
    if (!shipmentData) return;

    const customInputs = customInputFields.map((field) => {
      const existingField = shipmentData.customInputFields?.find((it) => it.key === field);
      if (!existingField) {
        return { key: field, value: '' };
      }
      return existingField;
    });

    reset({
      ...shipmentData,
      customInputFields: customInputs,
      // 既存 item を form state に積む際、画面 state 専用フィールドを初期化する。
      // weightKg / volumeM3 が既に値を持つ item は「ユーザー編集済み」として
      // 自動補完で上書きしないように manuallyEdited = true で初期化する。
      shipmentOrderItems: shipmentData.shipmentOrderItems.map((item) => ({
        ...item,
        entity: null,
        weightKgManuallyEdited: item.weightKg !== '' && item.weightKg != null,
        volumeM3ManuallyEdited: item.volumeM3 !== '' && item.volumeM3 != null,
      })),
      loadingOn: getEarliestDateFormatted(
        shipmentData.shipmentOrderLoadingWaypoint.shipmentOrderWaypointLoadingTimes
      ),
      loadingTimeSlot: 'カスタム',
      unloadingOn: getEarliestDateFormatted(
        shipmentData.shipmentOrderUnloadingWaypoint.shipmentOrderWaypointUnloadingTimes
      ),
      unloadingTimeSlot: 'カスタム',
    });
    calculateTotalBillingAmount();
  }, [shipmentData, isEditing, customInputFields, reset, calculateTotalBillingAmount]);

  const legRunItems: ShipmentLegRunItem[] = useMemo(() => {
    if (!shipmentData) return [];
    return buildShipmentLegRunItems(shipmentData);
  }, [shipmentData]);

  const splitLeg = useMemo(
    () => (shipmentData?.wasSplit ? findSplitLeg(shipmentData.shipmentOrderLegs ?? []) : null),
    [shipmentData],
  );
  const splitRuns = useMemo(() => (splitLeg ? splitRunsOf(splitLeg) : []), [splitLeg]);
  const splitMode = splitRuns.length > 0;

  const handleRestoreSplit = () => {
    const message = modified
      ? '未保存の変更があります。破棄して分割を元に戻しますか？'
      : '分割した案件を復元します。よろしいですか？';
    if (!window.confirm(message)) return;

    const targetId = splitRuns[0]?.id;
    if (targetId == null) return;

    restoreSplitLegRun.mutate(targetId, {
      onSuccess: () => {
        // 復元成功後は詳細を閉じない。hook が SHIPMENT_ORDERS_QUERY_KEY を invalidate するため
        // 詳細クエリ（[SHIPMENT_ORDERS_QUERY_KEY, shipmentId]）も自動再取得される。
        // 再取得した非分割データを view モードで表示するため編集状態をクリアする。
        setIsEditing(false);
        setModified(false);
        enqueueSnackbar('分割した案件を元に戻しました', { variant: 'success' });
      },
      onError: () => {
        enqueueSnackbar('分割した案件を元に戻す処理でエラーが発生しました', { variant: 'error' });
      },
    });
  };

  // 商品追加
  const handleAppendItem = () => {
    const clientKey = nextClientKey();
    appendItem({
      productCode: '',
      productName: '',
      itemPackingStyle: null,
      quantity: '',
      weightKg: '',
      weightG: '',
      volumeM3: '',
      volumeMm3: '',
      lotNumber: '',
      customOrderNumber: '',
      entity: null,
      weightKgManuallyEdited: false,
      volumeM3ManuallyEdited: false,
      clientKey,
    });
  };

  // 商品削除
  const handleRemoveItem = (index: number) => {
    removeItem(index);
  };

  // shipmentDataが変更されたらeditableLegRunItemsを更新
  useEffect(() => {
    if (!shipmentData) return;
    setEditableLegRunItems(legRunItems);
  }, [legRunItems, shipmentData]);

  // LEG単位の請求情報を更新する関数
  const handleUpdateLegRunBilling = useCallback((
    index: number,
    field: keyof ShipmentOrderBillingAmountEntity,
    value: string | number
  ) => {
    setEditableLegRunItems(prev => {
      const newItems = [...prev];
      if (!newItems[index].billingAmount) {
        newItems[index].billingAmount = {
          basicFeeYen: null,
          ancillaryFeeYen: null,
          expensesFeeYen: null,
          highwayFeeYen: null,
          loadingFeeYen: null,
          relayFeeYen: null,
          surchargeFeeYen: null,
          unloadingFeeYen: null,
          waitingTimeFeeYen: null,
          ancillaryContent: '',
        };
      }

      newItems[index].billingAmount[field] = value as never;

      // 請求金額合計を再計算
      const billingAmount = newItems[index].billingAmount;
      newItems[index].billingAmountTotal = [
        billingAmount.basicFeeYen || 0,
        billingAmount.ancillaryFeeYen || 0,
        billingAmount.expensesFeeYen || 0,
        billingAmount.highwayFeeYen || 0,
        billingAmount.loadingFeeYen || 0,
        billingAmount.relayFeeYen || 0,
        billingAmount.surchargeFeeYen || 0,
        billingAmount.unloadingFeeYen || 0,
        billingAmount.waitingTimeFeeYen || 0,
      ].filter(it => it).reduce((acc, fee) => acc + fee, 0);

      return newItems;
    });
  }, []);

  // LEG単位の支払い情報を更新する関数
  const handleUpdateLegRunPayment = useCallback((
    index: number,
    field: keyof ShipmentOrderLegRunPaymentAmountEntity,
    value: string | number
  ) => {
    setEditableLegRunItems(prev => {
      const newItems = [...prev];
      if (!newItems[index].paymentAmount) {
        newItems[index].paymentAmount = {
          basicFeeYen: null,
          ancillaryFeeYen: null,
          expensesFeeYen: null,
          highwayFeeYen: null,
          loadingFeeYen: null,
          relayFeeYen: null,
          surchargeFeeYen: null,
          unloadingFeeYen: null,
          waitingTimeFeeYen: null,
          ancillaryContent: '',
        };
      }

      newItems[index].paymentAmount[field] = value as never;

      // 支払金額合計を再計算
      const paymentAmount = newItems[index].paymentAmount;
      newItems[index].paymentAmountTotal = [
        paymentAmount.basicFeeYen || 0,
        paymentAmount.ancillaryFeeYen || 0,
        paymentAmount.expensesFeeYen || 0,
        paymentAmount.highwayFeeYen || 0,
        paymentAmount.loadingFeeYen || 0,
        paymentAmount.relayFeeYen || 0,
        paymentAmount.surchargeFeeYen || 0,
        paymentAmount.unloadingFeeYen || 0,
        paymentAmount.waitingTimeFeeYen || 0,
      ].filter(it => it).reduce((acc, fee) => acc + fee, 0);

      return newItems;
    });
  }, []);

  const legRunColumns: GridColDef[] = [
    {
      field: "id",
      headerName: "LEG ID",
      width: 120,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1}>
          <TruckIcon fontSize="small" />
          {params.value}
        </Box>
      )
    },
    { field: "sectionType", headerName: "区間種別", width: 100 },
    { field: "deliveryMethod", headerName: "配送形態", width: 100 },
    { field: "shipperName", headerName: "荷主名", width: 120 },
    { field: "route", headerName: "区間", width: 200 },
    { field: "loadingAddress", headerName: "積地住所", width: 180 },
    { field: "unloadingAddress", headerName: "降地住所", width: 180 },
    { field: "loadingOn", headerName: "積日", width: 100 },
    { field: "unloadingOn", headerName: "降日", width: 100 },
    { field: "loadingTimeWindow", headerName: "積地指定時間", width: 120 },
    { field: "unloadingTimeWindow", headerName: "降地指定時間", width: 120 },
    { field: "loadingStayingMinutes", headerName: "積地滞在時間", width: 110 },
    { field: "unloadingStayingMinutes", headerName: "降地滞在時間", width: 110 },
    { field: "quantity", headerName: "数量", width: 80, type: "number" },
    { field: "weightKg", headerName: "重量", width: 80, type: "number" },
    { field: "volumeM3", headerName: "体積", width: 80, type: "number" },
    {
      field: "billingAmountTotal",
      headerName: "請求金額合計",
      width: 120,
      type: "number",
      valueFormatter: (value: number) => `¥${value?.toLocaleString()}`
    },
    {
      field: "paymentAmountTotal",
      headerName: "支払金額合計",
      width: 120,
      type: "number",
      valueFormatter: (value: number) => `¥${value?.toLocaleString()}`
    },
    {
      field: "dispatchStatus",
      headerName: "ステータス",
      width: 100,
      renderCell: (params) => (
        <Chip
          label={String(params.value)}
          size="small"
          color={params.value === "配車済" ? "success" : "warning"}
        />
      )
    },
    { field: "carrierCompany", headerName: "運送会社", width: 120 },
    { field: "operatingCarrier", headerName: "実走行会社", width: 120 },
    { field: "truckNumber", headerName: "車番", width: 120 },
    { field: "driverName", headerName: "ドライバー", width: 100 },
    { field: "hierarchyLevel", headerName: "請負階層", width: 100 },
    { field: "planningLicenseName", headerName: "配車担当者", width: 120 }
  ];

  const viewItemColumns: GridColDef[] = [
    { field: "productCode", headerName: "商品コード", width: 120 },
    { field: "productName", headerName: "品名", width: 180 },
    { field: "itemPackingStyle", headerName: "荷姿", width: 100 },
    { field: "quantity", headerName: "数量", width: 80, type: "number" },
    { field: "weightKg", headerName: "重量(kg)", width: 100, type: "number" },
    { field: "volumeM3", headerName: "体積(㎥)", width: 100, type: "number" },
    { field: "lotNumber", headerName: "ロットNo", width: 120 },
    { field: "customOrderNumber", headerName: "特注No", width: 120 }
  ];

  const handleFormSubmit = (data: ShipmentDetailFormData) => {
    // ローディング状態を開始
    setIsSaving(true);

    // 積地時間情報の処理
    const originalLoadingDateTimes = shipmentData.shipmentOrderLoadingWaypoint.shipmentOrderWaypointLoadingTimes;
    const modifiedLoadingDateTimes = getValues('shipmentOrderLoadingWaypoint.shipmentOrderWaypointLoadingTimes');
    const shipmentOrderWaypointLoadingTimes = processWaypointTimes(
      originalLoadingDateTimes,
      modifiedLoadingDateTimes
    );

    // 降地時間情報の処理
    const originalUnloadingDateTimes = shipmentData.shipmentOrderUnloadingWaypoint.shipmentOrderWaypointUnloadingTimes;
    const modifiedUnloadingDateTimes = getValues('shipmentOrderUnloadingWaypoint.shipmentOrderWaypointUnloadingTimes');
    const shipmentOrderWaypointUnloadingTimes = processWaypointTimes(
      originalUnloadingDateTimes,
      modifiedUnloadingDateTimes
    );

    // 積日・降日 180日制限バリデーション（空の時間枠は範囲計算から除外）
    if (!validateLoadingUnloadingDateRangeForWaypointTimes(shipmentOrderWaypointLoadingTimes, shipmentOrderWaypointUnloadingTimes)) {
      enqueueSnackbar(LOADING_UNLOADING_DATE_RANGE_ERROR_MESSAGE, { variant: "error" });
      setIsSaving(false);
      return;
    }

    const customInputs = customInputFields.map((field, index) => {
      const val = getValues(`customInputFields.${index}.value`);
      return { key: field, value: val || '' };
    });

    const updatedItemIds = data.shipmentOrderItems.map((it) => it.id);
    data.shipmentOrderItems.push(
      ...shipmentData.shipmentOrderItems.filter((it) => !updatedItemIds.includes(it.id)).map((it) => ({ ...it, _destroy: true }))
    );

    // internal フィールド (entity / *ManuallyEdited / weightG / volumeMm3) は API に送らない。
    // rest 構文で除外することで `ShipmentOrderItemPayload` (Omit ベース) と実装が同期し、
    // 将来 `ShipmentOrderItemsEntity` にフィールドが追加されても自動追従する。
    // 削除アイテムには `_destroy` フラグを保持する。
    const sanitizedItems: ShipmentOrderItemPayload[] = data.shipmentOrderItems.map((it) => {
      const {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        entity,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        weightKgManuallyEdited,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        volumeM3ManuallyEdited,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        weightG,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        volumeMm3,
        ...base
      } = it;
      return ('_destroy' in it && it._destroy) ? { ...base, _destroy: true } : base;
    });

    // `sanitizedItems` は internal state を除外した payload 型のため、
    // `ShipmentDetailFormData.shipmentOrderItems` (= `ShipmentOrderItemsEntity[]`) が要求する
    // `weightKgManuallyEdited` / `volumeM3ManuallyEdited` を持たない。型を一旦 unknown
    // 経由で合わせている。`mutateAsync` の引数型を payload 型に変更すれば撤廃可能 (上の TODO と統合)。
    const updatedShipmentData = {
      ...data,
      shipmentOrderItems: sanitizedItems,
      customInputFields: customInputs,
      shipmentOrderLoadingWaypoint: {
        ...data.shipmentOrderLoadingWaypoint,
        shipmentOrderWaypointLoadingTimes,
      },
      shipmentOrderUnloadingWaypoint: {
        ...data.shipmentOrderUnloadingWaypoint,
        shipmentOrderWaypointUnloadingTimes,
      },
      shipmentOrderLegs: data.shipmentOrderLegs.map((leg) => ({
        ...leg,
        shipmentOrderLegRuns: leg.shipmentOrderLegRuns.map((run) => {
          const editedItem = editableLegRunItems.find(item => item.id === run.id);
          const base = editedItem
            ? {
              ...run,
              carrierCompany: editedItem.carrierCompany,
              operatingCarrier: editedItem.operatingCarrier,
              truckNumber: editedItem.truckNumber,
              driverName: editedItem.driverName,
              hierarchyLevel: editedItem.hierarchyLevel !== null ? Number(editedItem.hierarchyLevel) : null,
              useLtl: editedItem.useLtl,
              paymentAmount: editedItem.paymentAmount,
              billingAmount: editedItem.billingAmount,
            }
            : run;
          if (splitMode && run.wasSplit) {
            const { items: _omitItems, ...runWithoutItems } = base;
            return runWithoutItems;
          }
          return base;
        }),
      })),
    };

    updateShipmentOrder.mutateAsync(updatedShipmentData as unknown as ShipmentDetailFormData).then(() => {
      enqueueSnackbar('伝票を登録しました', { variant: 'success' });
    }).then(() => {
      onClose();
    }).catch((error: AxiosError<{ errors: string[] }>) => {
      console.log(error.response.data);
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        enqueueSnackbar(`伝票の登録に失敗しました: ${error.response.data.errors.join(', ') || error.message}`, { variant: 'error' });
      } else {
        enqueueSnackbar(`伝票の登録に失敗しました: ${error.message}`, { variant: 'error' });
      }
    }).finally(() => {
      // 処理が完了したらローディング状態を解除
      setIsSaving(false);
    });
  };

  const registeredPersonName = useMemo(() => {
    if (!shipmentData) return '';

    return shipmentData.registeredLicense?.licenseProfile?.personName || shipmentData.registeredLicense?.email || '';
  }, [shipmentData]);

  // トラックデータをTruckDriverIdAndName形式に変換
  const trucksOptions = useMemo(() => {
    if (!truckData) return [];
    return truckData.map(truck => ({
      id: truck.id,
      name: truck.licensePlateValue
    }));
  }, [truckData]);

  // ドライバーデータをTruckDriverIdAndName形式に変換
  const driversOptions = useMemo(() => {
    if (!driverData) return [];
    return driverData.map(driver => ({
      id: driver.id,
      name: driver.name
    }));
  }, [driverData]);

  // watchでフォームの値を監視して動的に更新
  const watchedAllowedTruckIds = watch('allowedTruckIds');
  const watchedDeniedDriverIds = watch('deniedDriverIds');

  // 指定トラックを動的に取得
  const allowedTrucks = useMemo(() => {
    if (!trucksOptions || !shipmentData) return [];

    const ids = watchedAllowedTruckIds || shipmentData.allowedTruckIds || [];
    return ids.map(id => {
      const truck = trucksOptions.find(t => t.id === id);
      return truck || null;
    }).filter((item): item is TruckDriverIdAndName => item !== null);
  }, [watchedAllowedTruckIds, trucksOptions, shipmentData]);

  // NGドライバーを動的に取得
  const deniedDrivers = useMemo(() => {
    if (!driversOptions || !shipmentData) return [];

    const ids = watchedDeniedDriverIds || shipmentData.deniedDriverIds || [];
    return ids.map(id => {
      const driver = driversOptions.find(d => d.id === id);
      return driver || null;
    }).filter((item): item is TruckDriverIdAndName => item !== null);
  }, [watchedDeniedDriverIds, driversOptions, shipmentData]);

  // 指定トラック変更ハンドラー
  const handleChangeAllowedTrucks = useCallback((_, value: TruckDriverIdAndName[]) => {
    setValue("allowedTruckIds", value.map(truck => truck.id), { shouldDirty: true });
    setValue("allowedTrucks", value, { shouldDirty: true });
    setModified(true);
  }, [setValue]);

  // NGドライバー変更ハンドラー
  const handleChangeDeniedDrivers = useCallback((_, value: TruckDriverIdAndName[]) => {
    setValue("deniedDriverIds", value.map(driver => driver.id), { shouldDirty: true });
    setValue("deniedDrivers", value, { shouldDirty: true });
    setModified(true);
  }, [setValue]);

  const onChangeTimeSlot = useCallback((field: 'loadingTimeSlot' | 'unloadingTimeSlot', value: string) => {
    setValue(field, value, { shouldDirty: true });
    const setting = timeSettings.find((it) => it.kind === value)
    if (!setting) return;

    const currentDate = field === 'loadingTimeSlot'
      ? getValues('loadingOn')
      : getValues('unloadingOn');

    // 時刻を適切な日時形式に変換
    const startDateTime = `${currentDate}T${setting.startAt}:00`;
    const endDateTime = `${currentDate}T${setting.endAt}:00`;

    if (field === 'loadingTimeSlot') {
      updateLoadingTime(0, { startAt: startDateTime, endAt: endDateTime, stayingSeconds: null });
      setValue('shipmentOrderLoadingWaypoint.shipmentOrderWaypointLoadingTimes.0.startAt', startDateTime, { shouldDirty: true });
      setValue('shipmentOrderLoadingWaypoint.shipmentOrderWaypointLoadingTimes.0.endAt', endDateTime, { shouldDirty: true });
    } else {
      updateUnloadingTime(0, { startAt: startDateTime, endAt: endDateTime, stayingSeconds: null });
      setValue('shipmentOrderUnloadingWaypoint.shipmentOrderWaypointUnloadingTimes.0.startAt', startDateTime, { shouldDirty: true });
      setValue('shipmentOrderUnloadingWaypoint.shipmentOrderWaypointUnloadingTimes.0.endAt', endDateTime, { shouldDirty: true });
    }
  }, [timeSettings]);

  const resetProductDetail = (index: number) => {
    setValue(`shipmentOrderItems.${index}.productCode`, null, { shouldDirty: true });
    setValue(`shipmentOrderItems.${index}.productName`, null, { shouldDirty: true });
    setValue(`shipmentOrderItems.${index}.entity`, null, { shouldDirty: true });
  };

  const updateProductDetail = (index: number, value: ProductEntity) => {
    setValue(`shipmentOrderItems.${index}.productCode`, value.code, { shouldDirty: true });
    setValue(`shipmentOrderItems.${index}.productName`, value.name, { shouldDirty: true });
    setValue(`shipmentOrderItems.${index}.entity`, value, { shouldDirty: true });
    applyAutoFill(index);
  };

  const applyAutoFill = (index: number) => {
    const item = getValues(`shipmentOrderItems.${index}`);
    const result = computeAutoFillItemWeightVolume({
      entity: item.entity,
      quantity: item.quantity,
      weightKgManuallyEdited: item.weightKgManuallyEdited ?? false,
      volumeM3ManuallyEdited: item.volumeM3ManuallyEdited ?? false,
    });
    if (result.weightKg !== undefined) {
      setValue(`shipmentOrderItems.${index}.weightKg`, result.weightKg, { shouldDirty: true });
    }
    if (result.volumeM3 !== undefined) {
      setValue(`shipmentOrderItems.${index}.volumeM3`, result.volumeM3, { shouldDirty: true });
    }
  };

  const updateProduct = (index: number, field: keyof ShipmentOrderItemsEntity, value: string | number) => {
    setValue(`shipmentOrderItems.${index}.${field}`, value, { shouldDirty: true });
  };

  const handleShipperChange = (_event: unknown, value: string | ShipperEntity | null) => {
    if (!value) {
      setValue("shipperName", "", { shouldDirty: true });
      return;
    }

    let selectedShipper: ShipperEntity | undefined;

    if (typeof value === "string") {
      // 自由入力の場合
      setValue("shipperName", value, { shouldDirty: true });

      // 同じ名前の荷主がshippersに存在するかチェック
      selectedShipper = shippers.find((shipper) => shipper.name === value);
    } else {
      selectedShipper = value;
    }
    if (selectedShipper) {
      setValue("shipperName", selectedShipper.name, { shouldDirty: true });
      setValue("shipperContactPerson", selectedShipper.contact_person || '', { shouldDirty: true });
      setValue("shipperPhoneNumber", selectedShipper.phone_number || '', { shouldDirty: true });
      setValue("shipperEmailAddress", selectedShipper.email_address || '', { shouldDirty: true });
    }
  };

  const handleDeleteShipper = (shipper: ShipperEntity) => {
    if (!window.confirm(`${shipper.name} を補完候補から削除します。よろしいですか？`)) return;
    deleteShipper.mutate(shipper.id);
  };

  const renderShipperOption = (
    props: HTMLAttributes<HTMLLIElement>,
    option: ShipperEntity
  ) => {
    const optionProps = { ...props } as HTMLAttributes<HTMLLIElement> & { key?: Key };
    const optionKey = optionProps.key;
    delete optionProps.key;

    return (
      <li key={optionKey} {...optionProps}>
        <ShipperOption option={option} disabled={deleteShipper.isPending} onDelete={handleDeleteShipper} />
      </li>
    );
  };

  const handleProductCodeChange = (value: string | ProductEntity | null, index: number) => {
    if (!value) {
      resetProductDetail(index);
      return;
    }

    let selectedProduct: ProductEntity | undefined;
    if (typeof value === 'string') {
      updateProduct(index, 'productCode', value);
      selectedProduct = products.find((product) => product.code === value);
    } else {
      selectedProduct = value;
    }

    if (selectedProduct) {
      updateProductDetail(index, selectedProduct);
    } else {
      setValue(`shipmentOrderItems.${index}.entity`, null, { shouldDirty: true });
    }
  };

  const handleProductNameChange = (value: string | ProductEntity | null, index: number) => {
    if (!value) {
      resetProductDetail(index);
      return;
    }

    let selectedProduct: ProductEntity | undefined;
    if (typeof value === 'string') {
      updateProduct(index, 'productName', value);
      selectedProduct = products.find((product) => product.name === value);
    } else {
      selectedProduct = value;
    }

    if (selectedProduct) {
      updateProductDetail(index, selectedProduct);
    } else {
      setValue(`shipmentOrderItems.${index}.entity`, null, { shouldDirty: true });
    }
  };

  // 地点選択時の共通処理
  const handleLocationChange = (
    type: 'loading' | 'unloading',
    value: string | PlaceEntity | null
  ) => {
    const nameField = type === 'loading' ? 'shipmentOrderLoadingWaypoint.name' : 'shipmentOrderUnloadingWaypoint.name';
    const addressField = type === 'loading' ? 'shipmentOrderLoadingWaypoint.address' : 'shipmentOrderUnloadingWaypoint.address';
    const latitudeField = type === 'loading' ? 'shipmentOrderLoadingWaypoint.latitude' : 'shipmentOrderUnloadingWaypoint.latitude';
    const longitudeField = type === 'loading' ? 'shipmentOrderLoadingWaypoint.longitude' : 'shipmentOrderUnloadingWaypoint.longitude';
    const skipGeocodingField = type === 'loading' ? 'shipmentOrderLoadingWaypoint.skipGeocoding' : 'shipmentOrderUnloadingWaypoint.skipGeocoding';

    if (!value) {
      setValue(nameField, '', { shouldDirty: true });
      setValue(skipGeocodingField, false, { shouldDirty: true });
      return;
    }

    let selectedPlace: PlaceEntity | undefined;
    if (typeof value === 'string') {
      setValue(nameField, value, { shouldDirty: true });
      setValue(skipGeocodingField, false, { shouldDirty: true });
      selectedPlace = places.find((place) => place.name === value);
    } else {
      selectedPlace = value;
    }

    if (selectedPlace) {
      setValue(nameField, selectedPlace.name, { shouldDirty: true });
      setValue(addressField, selectedPlace.address || '', { shouldDirty: true });
      setValue(latitudeField, selectedPlace.latitude || undefined, { shouldDirty: true });
      setValue(longitudeField, selectedPlace.longitude || undefined, { shouldDirty: true });
      if (selectedPlace.latitude && selectedPlace.longitude) {
        setValue(skipGeocodingField, true, { shouldDirty: true });
      }
    }
  };

    // LEG運行項目を更新する共通ハンドラ関数
  const handleUpdateLegRunItem = useCallback((
    legRunId: number | string,
    field: keyof ShipmentLegRunItem,
    value: string | number | null
  ) => {
    if (!partnerCompanyData?.data) return;

    setModified(true);
    let useLtl: boolean = undefined;
    if (field === 'carrierCompany') {
      const partnerCompany = partnerCompanyData.data.find((it) => it.name === value);
      useLtl = partnerCompany?.category === '路線会社';
    }
    let driverId: number = undefined;
    if (field === 'driverName') {
      const driver = driverData?.find((driverEntity) => driverEntity.name === value);
      driverId = driver ? driver.id : null;
    }
    setEditableLegRunItems((prevItems) =>
      prevItems.map((item) => {
        if (item.id === legRunId) {
          return {
            ...item,
            [field]: value,
            ...(useLtl !== undefined ? { useLtl } : {}), // useLtl が undefined でなければ設定する
            ...(driverId !== undefined ? { driverId } : {}), // driverId が undefined でなければ設定する
          };
        }
        return item;
      })
    );
  }, [driverData, partnerCompanyData?.data]);

  if (!shipmentData) return null;

  return (
    <Drawer
      anchor="right"
      open={Boolean(editShipmentDialogOpen)}
      onClose={handleClose}
      slotProps={{
        paper: {
          sx: {
            width: '60%',
            minWidth: '800px',
          }
        }
      }}
    >
      <Box sx={{ p: 2, height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Header */}
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h6" fontWeight="bold">
            伝票詳細: {getValues('code')}
          </Typography>
          <Box display="flex" gap={1}>
            {isEditing ? (
              <>
                <Button
                  variant="contained"
                  startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                  // eslint-disable-next-line @typescript-eslint/no-misused-promises
                  onClick={handleSubmit(handleFormSubmit)}
                  color="primary"
                  disabled={isSaving}
                >
                  {isSaving ? '保存中...' : '保存'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={handleClose}
                  disabled={isSaving}
                >
                  キャンセル
                </Button>
              </>
            ) : (
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => setIsEditing(true)}
              >
                編集
              </Button>
            )}
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        <Tabs value={detailTab} onChange={(_, value: number) => setDetailTab(value)} sx={{ mb: 2 }}>
          <Tab label="基本情報" />
          <Tab label="商品一覧" />
          <Tab label="LEG一覧" />
          <Tab label="配車管理" />
          <Tab label="請求・支払い情報" />
        </Tabs>

        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {detailTab === 0 && (
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" mb={3}>基本情報</Typography>

                {/* システム情報 */}
                <Box sx={{ mb: 4, p: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
                    システム情報
                  </Typography>
                  <Box display="grid" gridTemplateColumns="1fr 1fr" gap={3}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">伝票No</Typography>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          size="small"
                          {...register('code')}
                        />
                      ) : (
                        <Typography variant="body1" fontWeight="medium">{shipmentData.code}</Typography>
                      )}
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">案件登録者</Typography>
                      <Typography variant="body1" fontWeight="medium">{registeredPersonName}</Typography>
                    </Box>
                  </Box>
                </Box>

                {/* 荷主情報 */}
                <Box sx={{ mb: 4, p: 3, bgcolor: 'blue.50', borderRadius: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
                    荷主情報
                  </Typography>
                  <Box display="grid" gridTemplateColumns="1fr 1fr" gap={3}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">荷主名</Typography>
                      {isEditing ? (
                        <Controller
                          name="shipperName"
                          control={control}
                          render={({ field }) => (
                            <Autocomplete
                              {...field}
                              options={shippers}
                              getOptionLabel={(option) => typeof option === "string" ? option : option.name}
                              freeSolo
                              loading={shippersQuery.isLoading || deleteShipper.isPending}
                              value={field.value || null}
                              renderOption={renderShipperOption}
                              onChange={(_, value) => {
                                const newValue = typeof value === "string" ? value : (value ? value : '');
                                field.onChange(newValue);
                                handleShipperChange(_, value);
                              }}
                              onInputChange={(_, inputValue, reason) => {
                                if (reason === 'input') {
                                  field.onChange(inputValue);
                                  handleShipperChange(_, inputValue);
                                }
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  size="small"
                                  fullWidth
                                />
                              )}
                            />
                          )}
                        />
                      ) : (
                        <Typography variant="body1" fontWeight="medium">{shipmentData.shipperName}</Typography>
                      )}
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">荷主担当者</Typography>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          size="small"
                          { ...register('shipperContactPerson') }
                        />
                      ) : (
                        <Typography variant="body1" fontWeight="medium">{shipmentData.shipperContactPerson}</Typography>
                      )}
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">連絡先（電話）</Typography>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          size="small"
                          { ...register('shipperPhoneNumber') }
                        />
                      ) : (
                        <Typography variant="body1" fontWeight="medium">{shipmentData.shipperPhoneNumber}</Typography>
                      )}
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">連絡先（メール）</Typography>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          size="small"
                          { ...register('shipperEmailAddress') }
                        />
                      ) : (
                        <Typography variant="body1" fontWeight="medium">{shipmentData.shipperEmailAddress}</Typography>
                      )}
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">配送形態</Typography>
                      {isEditing ? (
                        <FormControl fullWidth size="small">
                          <Controller
                            name="deliveryMethod"
                            control={control}
                            render={({ field }) => (
                              <Select
                                {...field}
                                value={field.value || ''}
                                onChange={(e) => field.onChange(e.target.value)}
                              >
                                <MenuItem value="貸切">貸切</MenuItem>
                                <MenuItem value="共配">共配</MenuItem>
                                <MenuItem value="路線便">路線便</MenuItem>
                              </Select>
                            )}
                          />
                        </FormControl>
                      ) : (
                        <Typography variant="body1" fontWeight="medium">{shipmentData.deliveryMethod}</Typography>
                      )}
                    </Box>
                  </Box>
                </Box>

                {/* 区間情報 */}
                <Box sx={{ mb: 4, p: 3, bgcolor: 'indigo.50', borderRadius: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
                    区間情報
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={3}>
                    {/* 積地→降地（中継地がない場合） */}
                    {(shipmentData.shipmentOrderTransferWaypoints && shipmentData.shipmentOrderTransferWaypoints.length === 0) && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">区間種別</Typography>
                        {isEditing ? (
                          <FormControl fullWidth size="small">
                            <Controller
                              name="shipmentOrderLegs.0.stats"
                              control={control}
                              render={({ field }) => (
                                <Select
                                  {...field}
                                  value={field.value || '配送'}
                                  onChange={(e) => setValue('shipmentOrderLegs.0.stats', e.target.value)}
                                >
                                  <MenuItem value="集荷">集荷</MenuItem>
                                  <MenuItem value="横持ち">横持ち</MenuItem>
                                  <MenuItem value="配送">配送</MenuItem>
                                </Select>
                              )}
                            />
                          </FormControl>
                        ) : (
                          <Typography variant="body1" fontWeight="medium">
                            {shipmentData.shipmentOrderLegs[0].stats || "配送"}
                          </Typography>
                        )}
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          積地 → 降地
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* 積地情報 */}
                <Box sx={{ mb: 4, p: 3, bgcolor: 'green.50', borderRadius: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
                    積地情報
                  </Typography>
                  <Box display="grid" gridTemplateColumns="1fr 1fr" gap={3}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">積地名称</Typography>
                      {isEditing ? (
                        <Controller
                          name="shipmentOrderLoadingWaypoint.name"
                          control={control}
                          render={({ field }) => (
                            <Autocomplete
                              {...field}
                              options={pickupPlaces}
                              getOptionLabel={(option) => typeof option === "string" ? option : option.name}
                              freeSolo
                              loading={placesQuery.isLoading}
                              value={field.value || null}
                              onChange={(_, value) => {
                                field.onChange(value);
                                handleLocationChange('loading', value);
                              }}
                              onInputChange={(_, inputValue, reason) => {
                                if (reason === 'input') {
                                  field.onChange(inputValue);
                                  handleLocationChange('loading', inputValue);
                                }
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  size="small"
                                  fullWidth
                                  placeholder="地点マスタから選択または入力"
                                />
                              )}
                            />
                          )}
                        />
                      ) : (
                        <Typography variant="body1" fontWeight="medium">{shipmentData.shipmentOrderLoadingWaypoint.name}</Typography>
                      )}
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">積地住所</Typography>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          size="small"
                          {...register('shipmentOrderLoadingWaypoint.address')}
                          onChange={(e) => {
                            setValue('shipmentOrderLoadingWaypoint.address', e.target.value, { shouldDirty: true });
                            setValue('shipmentOrderLoadingWaypoint.skipGeocoding', false, { shouldDirty: true });
                          }}
                        />
                      ) : (
                        <Typography variant="body1" fontWeight="medium">{shipmentData.shipmentOrderLoadingWaypoint.address}</Typography>
                      )}
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">積日</Typography>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          size="small"
                          type="date"
                          {...register('loadingOn')}
                        />
                      ) : (
                        <Typography variant="body1" fontWeight="medium">{
                          getEarliestDateFormatted(
                            shipmentData.shipmentOrderLoadingWaypoint.shipmentOrderWaypointLoadingTimes
                          )
                        }</Typography>
                      )}
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">時間枠</Typography>
                      {isEditing ? (
                        <FormControl fullWidth size="small">
                          <Controller
                            name="loadingTimeSlot"
                            control={control}
                            render={({ field }) => (
                              <Select
                                {...field}
                                value={field.value || 'AM'}
                                onChange={(e) => { onChangeTimeSlot('loadingTimeSlot', e.target.value) }}
                              >
                                {timeSlotOptions.map((option) => (
                                  <MenuItem key={option} value={option}>{option}</MenuItem>
                                ))}
                              </Select>
                            )}
                          />
                        </FormControl>
                      ) : (
                        <Typography variant="body1" fontWeight="medium">{getValues('loadingTimeSlot')}</Typography>
                      )}
                    </Box>
                    {/* 時間枠（最大3つ） */}
                    <Box sx={{ gridColumn: '1 / -1' }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>時間枠</Typography>
                      <Box display="flex" flexDirection="column" gap={2}>
                        {loadingTimeFields.map((field, index) => (
                          <Box key={field.id} display="flex" alignItems="center" gap={2} flexWrap="wrap">
                            {isEditing ? (
                              <Box display="flex" gap={2} alignItems="center" flex={1}>
                                <Controller
                                  name={`shipmentOrderLoadingWaypoint.shipmentOrderWaypointLoadingTimes.${index}.date`}
                                  control={control}
                                  defaultValue={watch("loadingOn")}
                                  render={({ field: dateField }) => (
                                    <DatePicker
                                      label="日付"
                                      value={new Date(dateField.value ?? watch("loadingOn"))}
                                      onChange={(date) => {
                                        dateField.onChange(date ?? watch("loadingOn"));
                                        // 日付が変更されたら、startAtとendAtも更新
                                        const startTime = datetimeDecorator.toHourMinutes(new Date(getValues(`shipmentOrderLoadingWaypoint.shipmentOrderWaypointLoadingTimes.${index}.startAt`)));
                                        const endTime = datetimeDecorator.toHourMinutes(new Date(getValues(`shipmentOrderLoadingWaypoint.shipmentOrderWaypointLoadingTimes.${index}.endAt`)));
                                        const newDate = datetimeDecorator.toYyyyMmDd(new Date(date || watch("loadingOn")));
                                        if (startTime) {
                                          setValue(`shipmentOrderLoadingWaypoint.shipmentOrderWaypointLoadingTimes.${index}.startAt`, `${newDate}T${startTime}:00`, { shouldDirty: true });
                                        }
                                        if (endTime) {
                                          setValue(`shipmentOrderLoadingWaypoint.shipmentOrderWaypointLoadingTimes.${index}.endAt`, `${newDate}T${endTime}:00`, { shouldDirty: true });
                                        }
                                      }}
                                      format="yyyy/MM/dd"
                                      slotProps={{ textField: { size: "small", sx: { flex: 1 } } }}
                                    />
                                  )}
                                />
                                <Controller
                                  name={`shipmentOrderLoadingWaypoint.shipmentOrderWaypointLoadingTimes.${index}.startAt`}
                                  control={control}
                                  render={({ field: innerField }) => {
                                    return (
                                      <TextField
                                        {...innerField}
                                        type="time"
                                        placeholder="09:00"
                                        label="開始時間"
                                        size="small"
                                        fullWidth
                                        sx={{ flex: 1 }}
                                        slotProps={{ inputLabel: { shrink: true } }}
                                        value={datetimeDecorator.toHourMinutes(new Date(innerField.value))}
                                        onChange={(e) => {
                                          const dateValue = getValues(`shipmentOrderLoadingWaypoint.shipmentOrderWaypointLoadingTimes.${index}.date`);
                                          const currentDate = datetimeDecorator.toYyyyMmDd(new Date(dateValue || getValues('loadingOn')));
                                          const newDateTime = `${currentDate}T${e.target.value}:00`;
                                          innerField.onChange(newDateTime);
                                        }}
                                      />
                                    );
                                  }}
                                />
                                <Typography>〜</Typography>
                                <Controller
                                  name={`shipmentOrderLoadingWaypoint.shipmentOrderWaypointLoadingTimes.${index}.endAt`}
                                  control={control}
                                  render={({ field: innerField }) => (
                                    <TextField
                                      {...innerField}
                                      type="time"
                                      placeholder="09:00"
                                      label="終了時間"
                                      size="small"
                                      fullWidth
                                      sx={{ flex: 1 }}
                                      slotProps={{ inputLabel: { shrink: true } }}
                                      value={datetimeDecorator.toHourMinutes(new Date(innerField.value))}
                                      onChange={(e) => {
                                        const dateValue = getValues(`shipmentOrderLoadingWaypoint.shipmentOrderWaypointLoadingTimes.${index}.date`);
                                        const currentDate = datetimeDecorator.toYyyyMmDd(new Date(dateValue || getValues('loadingOn')));
                                        const newDateTime = `${currentDate}T${e.target.value}:00`;
                                        innerField.onChange(newDateTime);
                                      }}
                                    />
                                  )}
                                />
                                <IconButton
                                  size="small"
                                  color="error"
                                  disabled={loadingTimeFields.length >= 3}
                                  onClick={() => appendLoadingTime({ startAt: '', endAt: '', stayingSeconds: null })}
                                >
                                  <AddIcon />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="error"
                                  disabled={loadingTimeFields.length <= 1}
                                  onClick={() => removeLoadingTime(index)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                            ) : (
                              <Box display="flex" gap={1} alignItems="center">
                                <Typography variant="body1" fontWeight="medium">{
                                  datetimeDecorator.toYyyyMmDdHhMm(new Date(field.startAt))
                                }</Typography>
                                <Typography variant="body1">〜</Typography>
                                <Typography variant="body1" fontWeight="medium">{
                                  datetimeDecorator.toYyyyMmDdHhMm(new Date(field.endAt))
                                }</Typography>
                              </Box>
                            )}
                          </Box>
                        ))}
                      </Box>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">滞在時間</Typography>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          value={watch('shipmentOrderLoadingWaypoint.stayingMinutes') || ''}
                          onChange={(e) => {
                            const value = e.target.value === '' ? '' : parseInt(e.target.value) || 0;
                            setValue('shipmentOrderLoadingWaypoint.stayingSeconds', value === '' ? '' : value * 60, { shouldDirty: true });
                            setValue('shipmentOrderLoadingWaypoint.stayingMinutes', value, { shouldDirty: true });
                          }}
                          slotProps={{
                            input: {
                              endAdornment: <Typography variant="body2">分</Typography>
                            }
                          }}
                        />
                      ) : (
                        <Typography variant="body1" fontWeight="medium">{shipmentData.shipmentOrderLoadingWaypoint.stayingMinutes}分</Typography>
                      )}
                    </Box>
                  </Box>
                </Box>

                {/* 降地情報 */}
                <Box sx={{ mb: 4, p: 3, bgcolor: 'red.50', borderRadius: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
                    降地情報
                  </Typography>
                  <Box display="grid" gridTemplateColumns="1fr 1fr" gap={3}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">降地名称</Typography>
                      {isEditing ? (
                        <Controller
                          name="shipmentOrderUnloadingWaypoint.name"
                          control={control}
                          render={({ field }) => (
                            <Autocomplete
                              {...field}
                              options={deliveryPlaces}
                              getOptionLabel={(option) => typeof option === "string" ? option : option.name}
                              freeSolo
                              loading={placesQuery.isLoading}
                              value={field.value || null}
                              onChange={(_, value) => {
                                field.onChange(value);
                                handleLocationChange('unloading', value);
                              }}
                              onInputChange={(_, inputValue, reason) => {
                                if (reason === 'input') {
                                  field.onChange(inputValue);
                                  handleLocationChange('unloading', inputValue);
                                }
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  size="small"
                                  fullWidth
                                  placeholder="地点マスタから選択または入力"
                                />
                              )}
                            />
                          )}
                        />
                      ) : (
                        <Typography variant="body1" fontWeight="medium">{shipmentData.shipmentOrderUnloadingWaypoint.name}</Typography>
                      )}
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">降地住所</Typography>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          size="small"
                          {...register('shipmentOrderUnloadingWaypoint.address')}
                          onChange={(e) => {
                            setValue('shipmentOrderUnloadingWaypoint.address', e.target.value, { shouldDirty: true });
                            setValue('shipmentOrderUnloadingWaypoint.skipGeocoding', false, { shouldDirty: true });
                          }}
                        />
                      ) : (
                        <Typography variant="body1" fontWeight="medium">{shipmentData.shipmentOrderUnloadingWaypoint.address}</Typography>
                      )}
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">降日</Typography>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          size="small"
                          type="date"
                          {...register('unloadingOn')}
                        />
                      ) : (
                        getEarliestDateFormatted(
                            shipmentData.shipmentOrderUnloadingWaypoint.shipmentOrderWaypointUnloadingTimes
                          )
                      )}
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">降日</Typography>
                      {isEditing ? (
                        <FormControl fullWidth size="small">
                          <Controller
                            name="unloadingTimeSlot"
                            control={control}
                            render={({ field }) => (
                              <Select
                                {...field}
                                value={field.value || 'AM'}
                                onChange={(e) => { onChangeTimeSlot('unloadingTimeSlot', e.target.value) }}
                              >
                                {timeSlotOptions.map((option) => (
                                  <MenuItem key={option} value={option}>{option}</MenuItem>
                                ))}
                              </Select>
                            )}
                          />
                        </FormControl>
                      ) : (
                        <Typography variant="body1" fontWeight="medium">{getValues('unloadingTimeSlot')}</Typography>
                      )}
                    </Box>
                    {/* 時間枠（最大3つ） */}
                    <Box sx={{ gridColumn: '1 / -1' }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>時間枠</Typography>
                      <Box display="flex" flexDirection="column" gap={2}>
                        {unloadingTimeFields.map((field, index) => (
                          <Box key={field.id} display="flex" alignItems="center" gap={2} flexWrap="wrap">
                            {isEditing ? (
                              <Box display="flex" gap={2} alignItems="center" flex={1}>
                                <Controller
                                  name={`shipmentOrderUnloadingWaypoint.shipmentOrderWaypointUnloadingTimes.${index}.date`}
                                  control={control}
                                  defaultValue={watch("unloadingOn")}
                                  render={({ field: dateField }) => (
                                    <DatePicker
                                      label="日付"
                                      value={new Date(dateField.value ?? watch("unloadingOn"))}
                                      onChange={(date) => {
                                        dateField.onChange(date ?? watch("unloadingOn"));
                                        // 日付が変更されたら、startAtとendAtも更新
                                        const startTime = datetimeDecorator.toHourMinutes(new Date(getValues(`shipmentOrderUnloadingWaypoint.shipmentOrderWaypointUnloadingTimes.${index}.startAt`)));
                                        const endTime = datetimeDecorator.toHourMinutes(new Date(getValues(`shipmentOrderUnloadingWaypoint.shipmentOrderWaypointUnloadingTimes.${index}.endAt`)));
                                        const newDate = datetimeDecorator.toYyyyMmDd(new Date(date || watch("unloadingOn")));
                                        if (startTime) {
                                          setValue(`shipmentOrderUnloadingWaypoint.shipmentOrderWaypointUnloadingTimes.${index}.startAt`, `${newDate}T${startTime}:00`, { shouldDirty: true });
                                        }
                                        if (endTime) {
                                          setValue(`shipmentOrderUnloadingWaypoint.shipmentOrderWaypointUnloadingTimes.${index}.endAt`, `${newDate}T${endTime}:00`, { shouldDirty: true });
                                        }
                                      }}
                                      format="yyyy/MM/dd"
                                      slotProps={{ textField: { size: "small", sx: { flex: 1 } } }}
                                    />
                                  )}
                                />
                                <Controller
                                  name={`shipmentOrderUnloadingWaypoint.shipmentOrderWaypointUnloadingTimes.${index}.startAt`}
                                  control={control}
                                  render={({ field: innerField }) => (
                                    <TextField
                                      {...innerField}
                                      type="time"
                                      placeholder="09:00"
                                      label="開始時間"
                                      size="small"
                                      sx={{ flex: 1 }}
                                      slotProps={{ inputLabel: { shrink: true } }}
                                      value={datetimeDecorator.toHourMinutes(new Date(innerField.value))}
                                      onChange={(e) => {
                                        const dateValue = getValues(`shipmentOrderUnloadingWaypoint.shipmentOrderWaypointUnloadingTimes.${index}.date`);
                                        const currentDate = datetimeDecorator.toYyyyMmDd(new Date(dateValue || getValues('unloadingOn')));
                                        const newDateTime = `${currentDate}T${e.target.value}:00`;
                                        innerField.onChange(newDateTime);
                                      }}
                                    />
                                  )}
                                />
                                <Typography>〜</Typography>
                                <Controller
                                  name={`shipmentOrderUnloadingWaypoint.shipmentOrderWaypointUnloadingTimes.${index}.endAt`}
                                  control={control}
                                  render={({ field: innerField }) => (
                                    <TextField
                                      {...innerField}
                                      type="time"
                                      placeholder="09:00"
                                      label="終了時間"
                                      size="small"
                                      sx={{ flex: 1 }}
                                      slotProps={{ inputLabel: { shrink: true } }}
                                      value={datetimeDecorator.toHourMinutes(new Date(innerField.value))}
                                      onChange={(e) => {
                                        const dateValue = getValues(`shipmentOrderUnloadingWaypoint.shipmentOrderWaypointUnloadingTimes.${index}.date`);
                                        const currentDate = datetimeDecorator.toYyyyMmDd(new Date(dateValue || getValues('unloadingOn')));
                                        const newDateTime = `${currentDate}T${e.target.value}:00`;
                                        innerField.onChange(newDateTime);
                                      }}
                                    />
                                  )}
                                />
                                <IconButton
                                  size="small"
                                  color="error"
                                  disabled={unloadingTimeFields.length >= 3}
                                  onClick={() => appendUnloadingTime({ startAt: '', endAt: '', stayingSeconds: null })}
                                >
                                  <AddIcon />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="error"
                                  disabled={unloadingTimeFields.length <= 1}
                                  onClick={() => removeUnloadingTime(index)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                            ) : (
                              <Box display="flex" gap={1} alignItems="center">
                                <Typography variant="body1" fontWeight="medium">{
                                  datetimeDecorator.toYyyyMmDdHhMm(new Date(field.startAt))
                                }</Typography>
                                <Typography variant="body1">〜</Typography>
                                <Typography variant="body1" fontWeight="medium">{
                                  datetimeDecorator.toYyyyMmDdHhMm(new Date(field.endAt))
                                }</Typography>
                              </Box>
                            )}
                          </Box>
                        ))}
                      </Box>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">滞在時間</Typography>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          value={watch(`shipmentOrderUnloadingWaypoint.stayingMinutes`) || ''}
                          onChange={(e) => {
                            const value = e.target.value === '' ? '' : parseInt(e.target.value) || 0;
                            setValue('shipmentOrderUnloadingWaypoint.stayingSeconds', value === '' ? '' : value * 60, { shouldDirty: true });
                            setValue('shipmentOrderUnloadingWaypoint.stayingMinutes', parseInt(e.target.value), { shouldDirty: true });
                          }}
                          slotProps={{
                            input: { endAdornment: <Typography variant="body2">分</Typography> }
                          }}
                        />
                      ) : (
                        <Typography variant="body1" fontWeight="medium">{shipmentData.shipmentOrderUnloadingWaypoint.stayingMinutes}分</Typography>
                      )}
                    </Box>
                  </Box>
                </Box>

                {/* 貨物条件 */}
                <Box sx={{ mb: 4, p: 3, bgcolor: 'teal.50', borderRadius: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
                    貨物条件
                  </Typography>
                  <Box display="grid" gridTemplateColumns="1fr 1fr" gap={3}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">貨物種別</Typography>
                      {isEditing ? (
                        <FormControl fullWidth size="small">
                          <Select
                            value={watch('itemKlass') || ''}
                            onChange={(e) => setValue('itemKlass', e.target.value, { shouldDirty: true })}
                          >
                            <MenuItem value="">&nbsp;</MenuItem>
                            {ItemKlassOptions.map((option) => (
                              <MenuItem key={option} value={option}>{option}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <Typography variant="body1" fontWeight="medium">
                          {shipmentData.itemKlass}
                        </Typography>
                      )}
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">荷扱い</Typography>
                      {isEditing ? (
                        <FormControl fullWidth size="small">
                          <Select
                            value={watch('itemHandlingOfCargoStyle') || ''}
                            onChange={(e) => { setValue('itemHandlingOfCargoStyle', e.target.value, { shouldDirty: true }) }}
                          >
                            <MenuItem value="">&nbsp;</MenuItem>
                            {ItemHandlingOfCargoStyleOptions.map((option) => (
                              <MenuItem key={option} value={option}>{option}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <Typography variant="body1" fontWeight="medium">
                          {shipmentData.itemHandlingOfCargoStyle}
                        </Typography>
                      )}
                    </Box>
                    <Box>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="subtitle2" color="text.secondary">積み合わせ可否</Typography>
                        <Tooltip title="積み合わせ不可の場合は、同じ荷主・同じ貨物種別の荷物としか運べません">
                          <HelpOutlineIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        </Tooltip>
                      </Box>
                      {isEditing ? (
                        <FormControlLabel
                          control={
                            <Switch
                              checked={watch("itemCanBeMixed") ?? true}
                              onChange={(e) => setValue("itemCanBeMixed", e.target.checked, { shouldDirty: true })}
                              color="primary"
                            />
                          }
                          label="積み合わせ可否"
                        />
                      ) : (
                        <Typography variant="body1" fontWeight="medium">
                          {getValues('itemCanBeMixed') ? "積み合わせ可能" : "積み合わせ不可"}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>

                {/* 備考 */}
                <Box sx={{ mb: 4, p: 3, bgcolor: 'orange.50', borderRadius: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
                    備考
                  </Typography>
                  <Box>
                    {isEditing ? (
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        size="small"
                        {...register('memo')}
                        placeholder="備考を入力してください"
                      />
                    ) : (
                      <Typography variant="body1" fontWeight="medium" sx={{ minHeight: '72px' }}>
                        {getValues('memo')}
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* 任意項目情報 */}
                <Box sx={{ mb: 4, p: 3, bgcolor: 'lime.50', borderRadius: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
                    任意項目情報
                  </Typography>
                  <Box
                    display="grid"
                    gridTemplateColumns={`repeat(${customInputFields.length || 1}, 1fr)`}
                    gap={1}
                    sx={{ mb: 2 }}
                  >
                    {customInputFields.map((it, index) => {
                      const field = watch(`customInputFields`)?.find((field) => field.key === it);
                      const val = field ? field.value : '';

                      return (
                        <Box key={[it, index].join('-')}>
                          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>{it}</Typography>
                          {isEditing ? (
                            <TextField
                              fullWidth
                              multiline
                              rows={2}
                              key={index}
                              label={it}
                              size="small"
                              value={val}
                              onChange={(e) => {
                                setValue(`customInputFields.${index}.value`, e.target.value, { shouldDirty: true });
                              }}
                            />
                          ) : (
                            <Typography variant="body1" fontWeight="medium">
                              {val || '未入力'}
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>

                {/* 制約情報 */}
                <Box sx={{ mb: 4, p: 3, bgcolor: 'purple.50', borderRadius: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
                    制約情報
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={4}>
                    {/* 指定車両タイプ */}
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>指定車両タイプ</Typography>
                      {isEditing ? (
                        <Box display="flex" gap={1} flexWrap="wrap">
                          {DesignatedTruckKlassOptions.map((type) => (
                            <FormControlLabel
                              key={type}
                              control={
                                <Checkbox
                                  checked={(watch("designatedTruckKlasses") || []).includes(type)}
                                  onChange={(e) => {
                                    const currentTypes = watch("designatedTruckKlasses") || [];
                                    const newTypes = e.target.checked
                                      ? [...currentTypes, type]
                                      : currentTypes.filter(t => t !== type);
                                    setValue("designatedTruckKlasses", newTypes, { shouldDirty: true });
                                  }}
                                />
                              }
                              label={type}
                            />
                          ))}
                        </Box>
                      ) : (
                        <Box display="flex" gap={1} flexWrap="wrap">
                          {(getValues('designatedTruckKlasses') || []).length > 0 ? (
                            getValues('designatedTruckKlasses').map((type: string) => (
                              <Chip key={type} label={type} size="small" color="primary" variant="outlined" />
                            ))
                          ) : (
                            <Typography variant="body2" color="text.secondary">未指定</Typography>
                          )}
                        </Box>
                      )}
                    </Box>

                    {/* 指定車種 */}
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>指定車種</Typography>
                      {isEditing ? (
                        <Box display="flex" gap={1} flexWrap="wrap">
                          {DesignatedTruckCarModelOptions.map((model) => (
                            <FormControlLabel
                              key={model}
                              control={
                                <Checkbox
                                  checked={(watch("designatedTruckCarModels") || []).includes(model)}
                                  onChange={(e) => {
                                    const currentModels = watch("designatedTruckCarModels") || [];
                                    const newModels = e.target.checked
                                      ? [...currentModels, model]
                                      : currentModels.filter(m => m !== model);
                                    setValue("designatedTruckCarModels", newModels, { shouldDirty: true });
                                  }}
                                />
                              }
                              label={model}
                              sx={{
                                whiteSpace: 'nowrap',
                                minWidth: 'fit-content',
                                flexShrink: 0,
                                mb: 0,
                              }}
                            />
                          ))}
                        </Box>
                      ) : (
                        <Box display="flex" gap={1} flexWrap="wrap">
                          {(getValues('designatedTruckCarModels') || []).length > 0 ? (
                            getValues('designatedTruckCarModels').map((model: string) => (
                              <Chip key={model} label={model} size="small" color="primary" variant="outlined" />
                            ))
                          ) : (
                            <Typography variant="body2" color="text.secondary">未指定</Typography>
                          )}
                        </Box>
                      )}
                    </Box>

                    {/* 荷台高さ */}
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>荷台高さ</Typography>
                      {isEditing ? (
                        <Box display="flex" gap={1} flexWrap="wrap">
                          {DesignatedTruckLoadingPlatformHeightOptions.map((height) => (
                            <FormControlLabel
                              key={height}
                              control={
                                <Checkbox
                                  checked={(watch("designatedTruckLoadingPlatformHeights") || []).includes(height)}
                                  onChange={(e) => {
                                    const currentHeights = watch("designatedTruckLoadingPlatformHeights") || [];
                                    const newHeights = e.target.checked
                                      ? [...currentHeights, height]
                                      : currentHeights.filter(h => h !== height);
                                    setValue("designatedTruckLoadingPlatformHeights", newHeights, { shouldDirty: true });
                                  }}
                                />
                              }
                              label={height}
                            />
                          ))}
                        </Box>
                      ) : (
                        <Box display="flex" gap={1} flexWrap="wrap">
                          {(getValues("designatedTruckLoadingPlatformHeights") || []).length > 0 ? (
                            getValues("designatedTruckLoadingPlatformHeights").map((height: string) => (
                              <Chip key={height} label={height} size="small" color="primary" variant="outlined" />
                            ))
                          ) : (
                            <Typography variant="body2" color="text.secondary">未指定</Typography>
                          )}
                        </Box>
                      )}
                    </Box>

                    {/* 荷台幅 */}
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>荷台幅</Typography>
                      {isEditing ? (
                        <Box display="flex" gap={1} flexWrap="wrap">
                          {DesignatedTruckLoadingPlatformWidthOptions.map((width) => (
                            <FormControlLabel
                              key={width}
                              control={
                                <Checkbox
                                  checked={(watch("designatedTruckLoadingPlatformWidths") || []).includes(width)}
                                  onChange={(e) => {
                                    const currentWidths = watch("designatedTruckLoadingPlatformWidths") || [];
                                    const newWidths = e.target.checked
                                      ? [...currentWidths, width]
                                      : currentWidths.filter(w => w !== width);
                                    setValue("designatedTruckLoadingPlatformWidths", newWidths, { shouldDirty: true });
                                  }}
                                />
                              }
                              label={width}
                            />
                          ))}
                        </Box>
                      ) : (
                        <Box display="flex" gap={1} flexWrap="wrap">
                          {(getValues('designatedTruckLoadingPlatformWidths') || []).length > 0 ? (
                            getValues('designatedTruckLoadingPlatformWidths').map((width: string) => (
                              <Chip key={width} label={width} size="small" color="primary" variant="outlined" />
                            ))
                          ) : (
                            <Typography variant="body2" color="text.secondary">未指定</Typography>
                          )}
                        </Box>
                      )}
                    </Box>

                    {/* 荷台長さ */}
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>荷台長さ</Typography>
                      {isEditing ? (
                        <Box display="flex" gap={1} flexWrap="wrap">
                          {DesignatedTruckLoadingPlatformLengthOptions.map((length) => (
                            <FormControlLabel
                              key={length}
                              control={
                                <Checkbox
                                  checked={(watch("designatedTruckLoadingPlatformLengths") || []).includes(length)}
                                  onChange={(e) => {
                                    const currentLengths = watch("designatedTruckLoadingPlatformLengths") || [];
                                    const newLengths = e.target.checked
                                      ? [...currentLengths, length]
                                      : currentLengths.filter(l => l !== length);
                                    setValue("designatedTruckLoadingPlatformLengths", newLengths, { shouldDirty: true });
                                  }}
                                />
                              }
                              label={length}
                            />
                          ))}
                        </Box>
                      ) : (
                        <Box display="flex" gap={1} flexWrap="wrap">
                          {(getValues('designatedTruckLoadingPlatformLengths') || []).length > 0 ? (
                            getValues('designatedTruckLoadingPlatformLengths').map((length: string) => (
                              <Chip key={length} label={length} size="small" color="primary" variant="outlined" />
                            ))
                          ) : (
                            <Typography variant="body2" color="text.secondary">未指定</Typography>
                          )}
                        </Box>
                      )}
                    </Box>

                    {/* 床仕様 */}
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>床仕様</Typography>
                      {isEditing ? (
                        <Box display="flex" gap={1} flexWrap="wrap">
                          {DesignatedTruckFloorSpecificationOptions.map((floor) => (
                            <FormControlLabel
                              key={floor}
                              control={
                                <Checkbox
                                  checked={(watch("designatedTruckFloorSpecifications") || []).includes(floor)}
                                  onChange={(e) => {
                                    const currentFloors = watch("designatedTruckFloorSpecifications") || [];
                                    const newFloors = e.target.checked
                                      ? [...currentFloors, floor]
                                      : currentFloors.filter(f => f !== floor);
                                    setValue("designatedTruckFloorSpecifications", newFloors, { shouldDirty: true });
                                  }}
                                />
                              }
                              label={floor}
                            />
                          ))}
                        </Box>
                      ) : (
                        <Box display="flex" gap={1} flexWrap="wrap">
                          {(getValues('designatedTruckFloorSpecifications') || []).length > 0 ? (
                            getValues('designatedTruckFloorSpecifications').map((floor: string) => (
                              <Chip key={floor} label={floor} size="small" color="primary" variant="outlined" />
                            ))
                          ) : (
                            <Typography variant="body2" color="text.secondary">未指定</Typography>
                          )}
                        </Box>
                      )}
                    </Box>

                    {/* 装置 */}
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>装置</Typography>
                      {isEditing ? (
                        <Box display="flex" gap={1} flexWrap="wrap">
                          {DesignatedTruckFeatureOptions.map((equipment) => (
                            <FormControlLabel
                              key={equipment}
                              control={
                                <Checkbox
                                  checked={(watch("designatedTruckFeatures") || []).includes(equipment)}
                                  onChange={(e) => {
                                    const currentFeatures = watch("designatedTruckFeatures") || [];
                                    const newFeatures = e.target.checked
                                      ? [...currentFeatures, equipment]
                                      : currentFeatures.filter(eq => eq !== equipment);
                                    setValue("designatedTruckFeatures", newFeatures, { shouldDirty: true });
                                  }}
                                />
                              }
                              label={equipment}
                            />
                          ))}
                        </Box>
                      ) : (
                        <Box display="flex" gap={1} flexWrap="wrap">
                          {(getValues('designatedTruckFeatures') || []).length > 0 ? (
                            getValues('designatedTruckFeatures').map((equipment: string) => (
                              <Chip key={equipment} label={equipment} size="small" color="primary" variant="outlined" />
                            ))
                          ) : (
                            <Typography variant="body2" color="text.secondary">未指定</Typography>
                          )}
                        </Box>
                      )}
                    </Box>

                    {/* 指定トラック・NGドライバー */}
                    {isEditing ? (
                      <Box sx={{ gridColumn: '1 / -1' }}>
                        <TruckDriverConstraintsSelector
                          truckData={trucksOptions}
                          driverData={driversOptions}
                          allowedTrucks={allowedTrucks}
                          deniedDrivers={deniedDrivers}
                          onChangeAllowedTrucks={handleChangeAllowedTrucks}
                          onChangeDeniedDrivers={handleChangeDeniedDrivers}
                        />
                      </Box>
                    ) : (
                      <>
                        {/* 指定トラック */}
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>指定トラック</Typography>
                          <Box display="flex" gap={1} flexWrap="wrap">
                            {allowedTrucks.length === 0 ? (
                              <Typography variant="body2" color="text.secondary">未指定</Typography>
                            ) : (
                              allowedTrucks.map((truck) => (
                                <Chip key={truck.id} label={truck.name} size="small" color="success" variant="outlined" />
                              ))
                            )}
                          </Box>
                        </Box>

                        {/* NGドライバー*/}
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>NGドライバー</Typography>
                          <Box display="flex" gap={1} flexWrap="wrap">
                            {deniedDrivers.length === 0 ? (
                              <Typography variant="body2" color="text.secondary">未指定</Typography>
                            ) : (
                              deniedDrivers.map((driver) => (
                                <Chip key={driver.id} label={driver.name} size="small" color="error" variant="outlined" />
                              ))
                            )}
                          </Box>
                        </Box>
                      </>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )}

          {detailTab === 1 && (
            <Card sx={{ height: '100%', overflow: 'hidden' }}>
              <CardContent sx={{ height: '100%', overflow: 'auto', pb: 1 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6" fontWeight="bold">商品一覧</Typography>
                  {isEditing && !splitMode && (
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={handleAppendItem}
                      size="small"
                    >
                      商品追加
                    </Button>
                  )}
                </Box>

                {splitMode ? (
                  <Box>
                    {splitRuns.map((run, i) => (
                      <Box key={run.id} mb={3}>
                        <Typography variant="subtitle1" fontWeight="bold" mb={1}>分割伝票 {i + 1}</Typography>
                        <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                          <Table size="small" sx={{ tableLayout: 'fixed', minWidth: SPLIT_ITEM_TOTAL_WIDTH + SPLIT_ACTION_COL_WIDTH }}>
                            <colgroup>
                              {SPLIT_ITEM_COLUMNS.map((c) => (
                                <col key={c.label} style={{ width: c.width }} />
                              ))}
                              <col style={{ width: SPLIT_ACTION_COL_WIDTH }} />
                            </colgroup>
                            <TableHead>
                              <TableRow>
                                {SPLIT_ITEM_COLUMNS.map((c) => (
                                  <TableCell key={c.label} align={c.align}>{c.label}</TableCell>
                                ))}
                                <TableCell>操作</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {shipmentData.shipmentOrderItems.map((it, itemIdx) => {
                                const alloc = run.items.find((a) => a.shipmentOrderItemId === it.id);
                                const qty = alloc?.quantity ?? 0;
                                const wt = alloc?.weightG == null ? '' : (gToKg(alloc.weightG) ?? '');
                                const vol = alloc?.volumeMm3 == null ? '' : (mm3ToM3(alloc.volumeMm3) ?? '');
                                return (
                                  <TableRow key={String(it.id)}>
                                    <TableCell><Typography variant="body2" noWrap>{it.productCode}</Typography></TableCell>
                                    <TableCell><Typography variant="body2" noWrap>{it.productName}</Typography></TableCell>
                                    <TableCell><Typography variant="body2" noWrap>{it.itemPackingStyle}</Typography></TableCell>
                                    <TableCell align="right"><Typography variant="body2">{qty}</Typography></TableCell>
                                    <TableCell align="right"><Typography variant="body2">{wt}</Typography></TableCell>
                                    <TableCell align="right"><Typography variant="body2">{vol}</Typography></TableCell>
                                    <TableCell><Typography variant="body2" noWrap>{it.lotNumber}</Typography></TableCell>
                                    <TableCell><Typography variant="body2" noWrap>{it.customOrderNumber}</Typography></TableCell>
                                    <TableCell>
                                      {i === 0 && itemIdx === 0 && (
                                        <Button
                                          variant="outlined"
                                          color="error"
                                          size="small"
                                          onClick={handleRestoreSplit}
                                          disabled={isSaving || restoreSplitLegRun.isPending}
                                        >
                                          分割を戻す
                                        </Button>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    ))}
                    {/* 伝票合計: 保存済みの shipmentOrderItems を表示。API が保存時に分割 run の Σ で
                        再計算するため、各分割セクションの合計と一致する（Σ 書き戻し方式）。 */}
                    <Typography variant="subtitle1" fontWeight="bold" mb={1}>伝票合計</Typography>
                    <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                      <Table size="small" sx={{ tableLayout: 'fixed', minWidth: SPLIT_ITEM_TOTAL_WIDTH }}>
                        <colgroup>
                          {SPLIT_ITEM_COLUMNS.map((c) => (
                            <col key={c.label} style={{ width: c.width }} />
                          ))}
                        </colgroup>
                        <TableHead>
                          <TableRow>
                            {SPLIT_ITEM_COLUMNS.map((c) => (
                              <TableCell key={c.label} align={c.align}>{c.label}</TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {shipmentData.shipmentOrderItems.map((it) => (
                            <TableRow key={it.id}>
                              <TableCell><Typography variant="body2" noWrap>{it.productCode}</Typography></TableCell>
                              <TableCell><Typography variant="body2" noWrap>{it.productName}</Typography></TableCell>
                              <TableCell><Typography variant="body2" noWrap>{it.itemPackingStyle}</Typography></TableCell>
                              <TableCell align="right"><Typography variant="body2">{it.quantity}</Typography></TableCell>
                              <TableCell align="right"><Typography variant="body2">{it.weightKg}</Typography></TableCell>
                              <TableCell align="right"><Typography variant="body2">{it.volumeM3}</Typography></TableCell>
                              <TableCell><Typography variant="body2" noWrap>{it.lotNumber}</Typography></TableCell>
                              <TableCell><Typography variant="body2" noWrap>{it.customOrderNumber}</Typography></TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                ) : isEditing ? (
                  <TableContainer component={Paper} sx={{ maxHeight: '70vh' }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>商品コード</TableCell>
                          <TableCell>品名</TableCell>
                          <TableCell>荷姿</TableCell>
                          <TableCell>数量</TableCell>
                          <TableCell>重量(kg)</TableCell>
                          <TableCell>体積(㎥)</TableCell>
                          <TableCell>ロットNo</TableCell>
                          <TableCell>特注No</TableCell>
                          <TableCell>操作</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {itemFields.map((item, index) => (
                          <TableRow key={item.id || index}>
                            <TableCell>
                              <Controller
                                name={`shipmentOrderItems.${index}.productCode`}
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                  <Autocomplete
                                    {...field}
                                    sx={{ minWidth: 120 }}
                                    options={products.filter((it) => it.code !== null)}
                                    getOptionLabel={(option) => typeof option === 'string' ? option: option.code}
                                    getOptionKey={(option) => typeof option === 'string' ? option : option.id}
                                    freeSolo
                                    loading={productsIsLoading}
                                    value={field.value || ''}
                                    onInputChange={(_, value) => {
                                      if (value) {
                                        updateProduct(index, "productCode", value);
                                      }
                                      // タイプ入力で entity と乖離したら自動補完を抑止するために entity を null に戻す。
                                      // 候補に再一致したときは onChange で再セットされる。
                                      const currentEntity = getValues(`shipmentOrderItems.${index}.entity`);
                                      if (currentEntity && currentEntity.code !== value) {
                                        setValue(`shipmentOrderItems.${index}.entity`, null, { shouldDirty: true });
                                      }
                                    }}
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        placeholder="商品コード"
                                        size="small"
                                        fullWidth
                                        error={!!error}
                                        helperText={error?.message}
                                      />
                                    )}
                                    onChange={(_, value) => handleProductCodeChange(value, index)}
                                    slotProps={{
                                      clearIndicator: {
                                        sx: { m: '-8px', }
                                      }
                                    }}
                                  />
                                )}
                              />
                            </TableCell>
                            <TableCell>
                              <Controller
                                name={`shipmentOrderItems.${index}.productName`}
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                  <Autocomplete
                                    {...field}
                                    sx={{ minWidth: 200 }}
                                    options={products.filter((it) => it.name !== null)}
                                    getOptionLabel={(option) => typeof option === "string" ? option : option.name}
                                    getOptionKey={(option) => typeof option === 'string' ? option : option.id}
                                    freeSolo
                                    loading={productsIsLoading}
                                    value={field.value || ''}
                                    onInputChange={(_, value) => {
                                      if (value) {
                                        updateProduct(index, "productName", value);
                                      }
                                      // タイプ入力で entity と乖離したら自動補完を抑止するために entity を null に戻す。
                                      const currentEntity = getValues(`shipmentOrderItems.${index}.entity`);
                                      if (currentEntity && currentEntity.name !== value) {
                                        setValue(`shipmentOrderItems.${index}.entity`, null, { shouldDirty: true });
                                      }
                                    }}
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        placeholder="品名"
                                        size="small"
                                        fullWidth
                                        error={!!error}
                                        helperText={error?.message}
                                      />
                                    )}
                                    onChange={(_, value) => handleProductNameChange(value, index)}
                                    slotProps={{
                                      clearIndicator: {
                                        sx: { m: '-8px', }
                                      }
                                    }}
                                  />
                                )}
                              />
                            </TableCell>
                            <TableCell>
                              <Controller
                                name={`shipmentOrderItems.${index}.itemPackingStyle`}
                                control={control}
                                render={({ field }) => (
                                  <Autocomplete
                                    freeSolo
                                    size="small"
                                    options={[...ItemPackageStyleOptions]}
                                    value={field.value || null}
                                    onChange={(_, newValue) => field.onChange(newValue ?? '')}
                                    onInputChange={(_, newValue) => field.onChange(newValue)}
                                    componentsProps={{ popper: { style: { width: 'fit-content' } } }}
                                    renderInput={(params) => <TextField {...params} size="small" />}
                                  />
                                )}
                              />
                            </TableCell>
                            <TableCell>
                              <Controller
                                name={`shipmentOrderItems.${index}.quantity`}
                                control={control}
                                render={({ field }) => (
                                  <TextField
                                    {...field}
                                    size="small"
                                    type="number"
                                    sx={{ minWidth: 80 }}
                                    onChange={(e) => {
                                      const v = e.target.value === '' ? '' : parseInt(e.target.value);
                                      field.onChange(v);
                                      applyAutoFill(index);
                                    }}
                                  />
                                )}
                              />
                            </TableCell>
                            <TableCell>
                              <Controller
                                name={`shipmentOrderItems.${index}.weightKg`}
                                control={control}
                                render={({ field }) => (
                                  <TextField
                                    {...field}
                                    size="small"
                                    type="number"
                                    sx={{ minWidth: 100 }}
                                    onChange={(e) => {
                                      const v = e.target.value === '' ? '' : parseFloat(e.target.value);
                                      field.onChange(v);
                                      // 値が入っているとき true、空にしたら false に戻る。
                                      // = ユーザーが手動入力した値は自動補完で上書きしないが、
                                      //   一度空にすれば再び自動補完を有効化する仕様 (設計書参照)。
                                      setValue(
                                        `shipmentOrderItems.${index}.weightKgManuallyEdited`,
                                        v !== '',
                                        { shouldDirty: false },
                                      );
                                    }}
                                  />
                                )}
                              />
                            </TableCell>
                            <TableCell>
                              <Controller
                                name={`shipmentOrderItems.${index}.volumeM3`}
                                control={control}
                                render={({ field }) => (
                                  <TextField
                                    {...field}
                                    size="small"
                                    type="number"
                                    sx={{ minWidth: 100 }}
                                    onChange={(e) => {
                                      const v = e.target.value === '' ? '' : parseFloat(e.target.value);
                                      field.onChange(v);
                                      // 値が入っているとき true、空にしたら false に戻る。
                                      // = ユーザーが手動入力した値は自動補完で上書きしないが、
                                      //   一度空にすれば再び自動補完を有効化する仕様 (設計書参照)。
                                      setValue(
                                        `shipmentOrderItems.${index}.volumeM3ManuallyEdited`,
                                        v !== '',
                                        { shouldDirty: false },
                                      );
                                    }}
                                  />
                                )}
                              />
                            </TableCell>
                            <TableCell>
                              <Controller
                                name={`shipmentOrderItems.${index}.lotNumber`}
                                control={control}
                                render={({ field }) => (
                                  <TextField
                                    {...field}
                                    size="small"
                                    sx={{ minWidth: 120 }}
                                  />
                                )}
                              />
                            </TableCell>
                            <TableCell>
                              <Controller
                                name={`shipmentOrderItems.${index}.customOrderNumber`}
                                control={control}
                                render={({ field }) => (
                                  <TextField
                                    {...field}
                                    size="small"
                                    sx={{ minWidth: 120 }}
                                  />
                                )}
                              />
                            </TableCell>
                            <TableCell>
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveItem(index)}
                                color="error"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Paper sx={{ width: '100%', height: '70vh' }}>
                    <DataGrid
                      rows={shipmentData.shipmentOrderItems}
                      columns={viewItemColumns}
                      hideFooter
                      sx={{
                        '& .MuiDataGrid-cell': {
                          borderBottom: '1px solid #e0e0e0'
                        }
                      }}
                    />
                  </Paper>
                )}
              </CardContent>
            </Card>
          )}

          {detailTab === 2 && (
            <ShipmentLegRunListCard rows={legRunItems} />
          )}

          {detailTab === 3 && (
            <Card sx={{ height: '100%', overflow: 'hidden' }}>
              <CardContent sx={{ height: '100%', overflow: 'auto', pb: 1 }}>
                <Typography variant="h6" fontWeight="bold" mb={3}>配車管理</Typography>

                {/* LEG単位の配車情報 */}
                {editableLegRunItems.map((legRun) => (
                  <Card key={legRun.id} sx={{ mb: 3, border: '1px solid', borderColor: 'grey.300' }}>
                    <CardContent>
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                        <Typography variant="h6" fontWeight="bold" color="primary.main">
                          {legRun.id} - {legRun.route}
                        </Typography>
                        <Chip
                          label={legRun.dispatchStatus}
                          color={legRun.dispatchStatus === "配車済" ? "success" : "warning"}
                          size="small"
                        />
                      </Box>

                      <Box display="grid" gridTemplateColumns="1fr 1fr" gap={3}>
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">運送会社（契約先）</Typography>
                          {isEditing ? (
                            <Autocomplete
                              freeSolo
                              fullWidth
                              options={partnerCompanyData?.data?.map((company) => company.name) || []}
                              value={legRun.carrierCompany || null}
                              onChange={(_, value) => handleUpdateLegRunItem(legRun.id, 'carrierCompany', value)}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  size="small"
                                  placeholder="運送会社を選択または入力"
                                  onChange={(e) => {
                                    handleUpdateLegRunItem(legRun.id, 'carrierCompany', e.target.value);
                                  }}
                                />
                              )}
                            />
                          ) : (
                            <Typography variant="body1" fontWeight="medium">
                              {legRun.carrierCompany || "未設定"}
                            </Typography>
                          )}
                        </Box>

                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">実走行会社</Typography>
                          {isEditing ? (
                            <Autocomplete
                              freeSolo
                              fullWidth
                              options={partnerCompanyData?.data?.map((company) => company.name) || []}
                              value={legRun.operatingCarrier || null}
                              onChange={(_, value) => handleUpdateLegRunItem(legRun.id, 'operatingCarrier', value)}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  size="small"
                                  value={legRun.operatingCarrier || ''}
                                  onChange={(e) => {
                                    handleUpdateLegRunItem(legRun.id, 'operatingCarrier', e.target.value);
                                  }}
                                />
                              )}
                            />
                          ) : (
                            <Typography variant="body1" fontWeight="medium">
                              {legRun.operatingCarrier || "未設定"}
                            </Typography>
                          )}
                        </Box>

                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">車番</Typography>
                          {isEditing ? (
                            <TextField
                              fullWidth
                              size="small"
                              value={legRun.truckNumber || ''}
                              onChange={(e) => {
                                handleUpdateLegRunItem(legRun.id, 'truckNumber', e.target.value);
                              }}
                            />
                          ) : (
                            <Typography variant="body1" fontWeight="medium">
                              {legRun.truckNumber || "未設定"}
                            </Typography>
                          )}
                        </Box>

                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">ドライバー</Typography>
                          {isEditing ? (
                            <Autocomplete
                              freeSolo
                              fullWidth
                              options={driverData?.map((it) => it.name) || []}
                              value={legRun.driverName || null}
                              onChange={(_, value) => { handleUpdateLegRunItem(legRun.id, 'driverName', value); }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  size="small"
                                  placeholder="ドライバーを選択または入力"
                                  onChange={(e) => {
                                    handleUpdateLegRunItem(legRun.id, 'driverName', e.target.value);
                                  }}
                                />
                              )}
                            />
                          ) : (
                            <Typography variant="body1" fontWeight="medium">
                              {legRun.driverName || "未設定"}
                            </Typography>
                          )}
                        </Box>

                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">請負階層</Typography>
                          {isEditing ? (
                            <FormControl fullWidth size="small">
                              <Select
                                value={legRun.hierarchyLevel || ''}
                                onChange={(e) => {
                                  const value = (e.target.value === '') ? null : e.target.value;
                                  handleUpdateLegRunItem(legRun.id, 'hierarchyLevel', value);
                                }}
                              >
                                <MenuItem value="">元請け</MenuItem>
                                <MenuItem value="1">1次請け</MenuItem>
                                <MenuItem value="2">2次請け</MenuItem>
                                <MenuItem value="3">3次請け</MenuItem>
                              </Select>
                            </FormControl>
                          ) : (
                            <Typography variant="body1" fontWeight="medium">
                              {legRun.hierarchyLevel ? `${legRun.hierarchyLevel}次請け` : "未設定"}
                            </Typography>
                          )}
                        </Box>

                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">配車担当者（自動発行）</Typography>
                          <Typography variant="body1" fontWeight="medium" color="primary.main">
                            {legRun.planningLicenseName || "システム自動"}
                          </Typography>
                        </Box>

                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">配車ステータス</Typography>
                          <Chip
                            label={legRun.dispatchStatus}
                            color={legRun.dispatchStatus === "配車済" ? "success" : "warning"}
                            size="small"
                            />
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </CardContent>
            </Card>
          )}

          {detailTab === 4 && (
            <Card sx={{ height: '100%', overflow: 'hidden' }}>
              <CardContent sx={{ height: '100%', overflow: 'auto', pb: 1 }}>
                <Typography variant="h6" fontWeight="bold" mb={3}>請求・支払い情報</Typography>

                {/* 請求金額合計 */}
                <Box sx={{ mb: 4, p: 3, bgcolor: 'success.50', borderRadius: 2, border: '2px solid', borderColor: 'success.main' }}>
                  <Typography variant="h6" color="success.main" fontWeight="bold" gutterBottom>
                    請求金額合計
                  </Typography>
                  <Typography variant="h4" color="success.main" fontWeight="bold">
                    ¥{editableLegRunItems.reduce((sum, leg) => sum + (leg.billingAmountTotal || 0), 0).toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    ({editableLegRunItems.length}件のLEGの合計)
                  </Typography>
                </Box>

                {/* LEG単位の請求情報 */}
                <Typography variant="h6" fontWeight="bold" mb={2}>LEG単位の請求情報</Typography>

                {editableLegRunItems.map((legRun, index) => (
                  <Card key={legRun.id} sx={{ mb: 3, border: '1px solid', borderColor: 'success.300' }}>
                    <CardContent>
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                        <Typography variant="h6" fontWeight="bold" color="success.main">
                          {[shipmentData.code, index + 1].join('-')} - {legRun.route}
                        </Typography>
                        <Chip
                          label={legRun.dispatchStatus}
                          color={legRun.dispatchStatus === "配車済" ? "success" : "warning"}
                          size="small"
                        />
                      </Box>

                      <Box display="grid" gridTemplateColumns="1fr 1fr 1fr 1fr" gap={2}>
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">請求金額合計</Typography>
                          <Typography variant="h6" color="success.main" fontWeight="bold">
                            ¥{legRun.billingAmountTotal?.toLocaleString() || "0"}
                          </Typography>
                        </Box>

                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">基本運賃</Typography>
                          {isEditing ? (
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              value={legRun.billingAmount?.basicFeeYen}
                              onChange={(e) => handleUpdateLegRunBilling(index, 'basicFeeYen', e.target.value === '' ? '' : parseInt(e.target.value) || 0)}
                            />
                          ) : (
                            <Typography variant="body1" fontWeight="medium">
                              ¥{legRun.billingAmount?.basicFeeYen?.toLocaleString() || "0"}
                            </Typography>
                          )}
                        </Box>

                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">サーチャージ料</Typography>
                          {isEditing ? (
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              value={legRun.billingAmount?.surchargeFeeYen}
                              onChange={(e) => handleUpdateLegRunBilling(index, 'surchargeFeeYen', e.target.value === '' ? '' : parseInt(e.target.value) || 0)}
                            />
                          ) : (
                            <Typography variant="body1" fontWeight="medium">
                              ¥{legRun.billingAmount?.surchargeFeeYen?.toLocaleString() || "0"}
                            </Typography>
                          )}
                        </Box>

                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">中継料</Typography>
                          {isEditing ? (
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              value={legRun.billingAmount?.relayFeeYen}
                              onChange={(e) => handleUpdateLegRunBilling(index, 'relayFeeYen', e.target.value === '' ? '' : parseInt(e.target.value) || 0)}
                            />
                          ) : (
                            <Typography variant="body1" fontWeight="medium">
                              ¥{legRun.billingAmount?.relayFeeYen?.toLocaleString() || "0"}
                            </Typography>
                          )}
                        </Box>

                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">高速代金</Typography>
                          {isEditing ? (
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              value={legRun.billingAmount?.highwayFeeYen}
                              onChange={(e) => handleUpdateLegRunBilling(index, 'highwayFeeYen', e.target.value === '' ? '' : parseInt(e.target.value) || 0)}
                            />
                          ) : (
                            <Typography variant="body1" fontWeight="medium">
                              ¥{legRun.billingAmount?.highwayFeeYen?.toLocaleString() || "0"}
                            </Typography>
                          )}
                        </Box>

                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">積込料金</Typography>
                          {isEditing ? (
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              value={legRun.billingAmount?.loadingFeeYen}
                              onChange={(e) => handleUpdateLegRunBilling(index, 'loadingFeeYen', e.target.value === '' ? '' : parseInt(e.target.value) || 0)}
                            />
                          ) : (
                            <Typography variant="body1" fontWeight="medium">
                              ¥{legRun.billingAmount?.loadingFeeYen?.toLocaleString() || "0"}
                            </Typography>
                          )}
                        </Box>

                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">付帯作業料金</Typography>
                          {isEditing ? (
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              value={legRun.billingAmount?.ancillaryFeeYen}
                              onChange={(e) => handleUpdateLegRunBilling(index, 'ancillaryFeeYen', e.target.value === '' ? '' : parseInt(e.target.value) || 0)}
                            />
                          ) : (
                            <Typography variant="body1" fontWeight="medium">
                              ¥{legRun.billingAmount?.ancillaryFeeYen?.toLocaleString() || "0"}
                            </Typography>
                          )}
                        </Box>

                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">待機料金</Typography>
                          {isEditing ? (
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              value={legRun.billingAmount?.waitingTimeFeeYen}
                              onChange={(e) => handleUpdateLegRunBilling(index, 'waitingTimeFeeYen', e.target.value === '' ? '' : parseInt(e.target.value) || 0)}
                            />
                          ) : (
                            <Typography variant="body1" fontWeight="medium">
                              ¥{legRun.billingAmount?.waitingTimeFeeYen?.toLocaleString() || "0"}
                            </Typography>
                          )}
                        </Box>

                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">取卸料</Typography>
                          {isEditing ? (
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              value={legRun.billingAmount?.unloadingFeeYen}
                              onChange={(e) => handleUpdateLegRunBilling(index, 'unloadingFeeYen', e.target.value === '' ? '' : parseInt(e.target.value) || 0)}
                            />
                          ) : (
                            <Typography variant="body1" fontWeight="medium">
                              ¥{legRun.billingAmount?.unloadingFeeYen?.toLocaleString() || "0"}
                            </Typography>
                          )}
                        </Box>

                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">諸経費</Typography>
                          {isEditing ? (
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              value={legRun.billingAmount?.expensesFeeYen}
                              onChange={(e) => handleUpdateLegRunBilling(index, 'expensesFeeYen', e.target.value === '' ? '' : parseInt(e.target.value) || 0)}
                            />
                          ) : (
                            <Typography variant="body1" fontWeight="medium">
                              ¥{legRun.billingAmount?.expensesFeeYen?.toLocaleString() || "0"}
                            </Typography>
                          )}
                        </Box>

                        <Box sx={{ gridColumn: "1 / -1" }}>
                          <Typography variant="subtitle2" color="text.secondary">付帯作業備考</Typography>
                          {isEditing ? (
                            <TextField
                              fullWidth
                              multiline
                              rows={2}
                              size="small"
                              value={legRun.billingAmount?.ancillaryContent || ''}
                              onChange={(e) => handleUpdateLegRunBilling(index, 'ancillaryContent', e.target.value)}
                            />
                          ) : (
                            <Typography variant="body1" fontWeight="medium">
                              {legRun.billingAmount?.ancillaryContent || "なし"}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))}

                {/* 支払金額合計 */}
                <Box sx={{ mb: 4, p: 3, bgcolor: 'primary.50', borderRadius: 2, border: '2px solid', borderColor: 'primary.main' }}>
                  <Typography variant="h6" color="primary.main" fontWeight="bold" gutterBottom>
                    支払金額合計
                  </Typography>
                  <Typography variant="h4" color="primary.main" fontWeight="bold">
                    ¥{legRunItems.reduce((sum, leg) => sum + (leg.paymentAmountTotal || 0), 0).toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    ({legRunItems.length}件のLEGの合計)
                  </Typography>
                </Box>

                {/* LEG単位の支払い情報 */}
                <Typography variant="h6" fontWeight="bold" mb={2}>LEG単位の支払い情報</Typography>

                {legRunItems.map((legRun, index) => (
                  <Card key={legRun.id} sx={{ mb: 3, border: '1px solid', borderColor: 'grey.300' }}>
                    <CardContent>
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                        <Typography variant="h6" fontWeight="bold" color="primary.main">
                          {[shipmentData.code, index + 1].join('-')} - {legRun.route}
                        </Typography>
                        <Chip
                          label={legRun.dispatchStatus}
                          color={legRun.dispatchStatus === "配車済" ? "success" : "warning"}
                          size="small"
                        />
                      </Box>

                      <Box display="grid" gridTemplateColumns="1fr 1fr 1fr 1fr" gap={2}>
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">支払金額合計</Typography>
                          <Typography variant="h6" color="primary.main" fontWeight="bold">
                            ¥{legRun.paymentAmountTotal?.toLocaleString() || "0"}
                          </Typography>
                        </Box>

                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">基本運賃</Typography>
                          {isEditing ? (
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              value={legRun.paymentAmount?.basicFeeYen}
                              onChange={(e) => handleUpdateLegRunPayment(index, 'basicFeeYen', e.target.value === '' ? '' : parseInt(e.target.value) || 0)}
                            />
                          ) : (
                            <Typography variant="body1" fontWeight="medium">
                              ¥{legRun.paymentAmount?.basicFeeYen?.toLocaleString() || "0"}
                            </Typography>
                          )}
                        </Box>

                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">サーチャージ料</Typography>
                          {isEditing ? (
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              value={legRun.paymentAmount?.surchargeFeeYen}
                              onChange={(e) => handleUpdateLegRunPayment(index, 'surchargeFeeYen', e.target.value === '' ? '' : parseInt(e.target.value) || 0)}
                            />
                          ) : (
                            <Typography variant="body1" fontWeight="medium">
                              ¥{legRun.paymentAmount.surchargeFeeYen?.toLocaleString() || "0"}
                            </Typography>
                          )}
                        </Box>

                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">中継料</Typography>
                          {isEditing ? (
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              value={legRun.paymentAmount?.relayFeeYen}
                              onChange={(e) => handleUpdateLegRunPayment(index, 'relayFeeYen', e.target.value === '' ? '' : parseInt(e.target.value) || 0)}
                            />
                          ) : (
                            <Typography variant="body1" fontWeight="medium">
                              ¥{legRun.paymentAmount.relayFeeYen?.toLocaleString() || "0"}
                            </Typography>
                          )}
                        </Box>

                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">高速代金</Typography>
                          {isEditing ? (
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              value={legRun.paymentAmount?.highwayFeeYen}
                              onChange={(e) => handleUpdateLegRunPayment(index, 'highwayFeeYen', e.target.value === '' ? '' : parseInt(e.target.value) || 0)}
                            />
                          ) : (
                            <Typography variant="body1" fontWeight="medium">
                              ¥{legRun.paymentAmount.highwayFeeYen?.toLocaleString() || "0"}
                            </Typography>
                          )}
                        </Box>

                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">積込料金</Typography>
                          {isEditing ? (
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              value={legRun.paymentAmount?.loadingFeeYen}
                              onChange={(e) => handleUpdateLegRunPayment(index, 'loadingFeeYen', e.target.value === '' ? '' : parseInt(e.target.value) || 0)}
                            />
                          ) : (
                            <Typography variant="body1" fontWeight="medium">
                              ¥{legRun.paymentAmount.loadingFeeYen?.toLocaleString() || "0"}
                            </Typography>
                          )}
                        </Box>

                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">付帯作業料金</Typography>
                          {isEditing ? (
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              value={legRun.paymentAmount?.ancillaryFeeYen}
                              onChange={(e) => handleUpdateLegRunPayment(index, 'ancillaryFeeYen', e.target.value === '' ? '' : parseInt(e.target.value) || 0)}
                            />
                          ) : (
                            <Typography variant="body1" fontWeight="medium">
                              ¥{legRun.paymentAmount.ancillaryFeeYen?.toLocaleString() || "0"}
                            </Typography>
                          )}
                        </Box>

                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">待機料金</Typography>
                          {isEditing ? (
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              value={legRun.paymentAmount?.waitingTimeFeeYen}
                              onChange={(e) => handleUpdateLegRunPayment(index, 'waitingTimeFeeYen', e.target.value === '' ? '' : parseInt(e.target.value) || 0)}
                            />
                          ) : (
                            <Typography variant="body1" fontWeight="medium">
                              ¥{legRun.paymentAmount.waitingTimeFeeYen?.toLocaleString() || "0"}
                            </Typography>
                          )}
                        </Box>

                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">取卸料</Typography>
                          {isEditing ? (
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              value={legRun.paymentAmount?.unloadingFeeYen}
                              onChange={(e) => handleUpdateLegRunPayment(index, 'unloadingFeeYen', e.target.value === '' ? '' : parseInt(e.target.value) || 0)}
                            />
                          ) : (
                            <Typography variant="body1" fontWeight="medium">
                              ¥{legRun.paymentAmount.unloadingFeeYen?.toLocaleString() || "0"}
                            </Typography>
                          )}
                        </Box>

                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">諸経費</Typography>
                          {isEditing ? (
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              value={legRun.paymentAmount?.expensesFeeYen}
                              onChange={(e) => handleUpdateLegRunPayment(index, 'expensesFeeYen', e.target.value === '' ? '' : parseInt(e.target.value) || 0)}
                            />
                          ) : (
                            <Typography variant="body1" fontWeight="medium">
                              ¥{legRun.paymentAmount.expensesFeeYen?.toLocaleString() || "0"}
                            </Typography>
                          )}
                        </Box>

                        <Box sx={{ gridColumn: "1 / -1" }}>
                          <Typography variant="subtitle2" color="text.secondary">付帯作業備考</Typography>
                          {isEditing ? (
                            <TextField
                              fullWidth
                              multiline
                              rows={2}
                              size="small"
                              value={legRun.paymentAmount?.ancillaryContent || ''}
                              onChange={(e) => handleUpdateLegRunPayment(index, 'ancillaryContent', e.target.value)}
                            />
                          ) : (
                            <Typography variant="body1" fontWeight="medium">
                              {legRun.paymentAmount?.ancillaryContent || "なし"}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </CardContent>
            </Card>
          )}
        </Box>
      </Box>
      <ConfirmDialog
        open={confirmDiaalogOpen}
        message="保存していない変更を破棄しますか？"
        handleOk={handleConfirmOK}
        handleCancel={handleConfirmCancel}
      />
    </Drawer>
  );
};
